import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye, EyeOff, X, Users, DollarSign, Zap,
  ChevronRight, LogOut, TrendingUp, AlertCircle, Loader2,
  Tag, ShieldOff, Shield, CreditCard, RefreshCw, CheckCircle,
  AlertTriangle, Activity, Filter, UserPlus, FileText, Send, RotateCcw, UserX,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_URL || "";

/* ── Admin API helpers ──────────────────────────────────── */
async function adminFetch(path, method = "GET", body) {
  const token = sessionStorage.getItem("trackin_admin_token") || "";
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      "x-admin-token": token,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

// Opens a PDF-returning admin endpoint in a new tab — adminFetch always
// parses JSON, so PDF bytes need their own raw fetch + blob URL.
async function adminOpenPdf(path) {
  const token = sessionStorage.getItem("trackin_admin_token") || "";
  const res = await fetch(`${API_BASE}${path}`, { headers: { "x-admin-token": token } });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Request failed");
  }
  const blob = await res.blob();
  window.open(URL.createObjectURL(blob), "_blank");
}

/* ── Helpers ─────────────────────────────────────────────── */
function fmtDate(iso) {
  return new Date(iso).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" });
}
function fmtTime(iso) {
  return new Date(iso).toLocaleString("en-IN", {
    day:"numeric", month:"short", year:"numeric",
    hour:"2-digit", minute:"2-digit", second:"2-digit", hour12:true,
    timeZone: "Asia/Kolkata",
  });
}

/* ── Stat card ──────────────────────────────────────────── */
function StatCard({ label, value, sub, color="#111827", icon:Icon, accent }) {
  return (
    <div style={{
      background:"#ffffff", border:`1px solid ${accent || "#E5E7EB"}`,
      borderRadius:12, padding:"18px 20px",
      boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
        <div style={{
          width:32, height:32, borderRadius:8, background: accent ? `${accent}18` : "#F3F4F6",
          display:"flex", alignItems:"center", justifyContent:"center",
        }}>
          <Icon size={15} color={accent || "#6B7280"} />
        </div>
        <span style={{ fontSize:12, fontWeight:500, color:"#6B7280" }}>{label}</span>
      </div>
      <div style={{ fontSize:26, fontWeight:800, color, lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:12, color:"#9CA3AF", marginTop:5 }}>{sub}</div>}
    </div>
  );
}

/* ── Pool usage bar ─────────────────────────────────────── */
function OwnerPoolBar({ issued }) {
  const OWNER_POOL = 10_000;
  const remaining  = Math.max(0, OWNER_POOL - issued);
  const pct        = Math.min((issued / OWNER_POOL) * 100, 100);
  const low        = remaining < 1000;
  const color      = pct >= 90 ? "#DC2626" : pct >= 70 ? "#D97706" : "#2563EB";
  return (
    <div style={{
      background:"#ffffff", border:`1px solid ${low ? "#FECACA" : "#E5E7EB"}`,
      borderRadius:12, padding:"18px 20px",
      boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
        <div style={{
          width:32, height:32, borderRadius:8, background: low ? "#FEF2F2" : "#EFF6FF",
          display:"flex", alignItems:"center", justifyContent:"center",
        }}>
          <Zap size={15} color={low ? "#DC2626" : "#2563EB"} />
        </div>
        <span style={{ fontSize:12, fontWeight:500, color:"#6B7280" }}>Owner API Pool</span>
        {low && <span style={{ marginLeft:"auto", fontSize:10, fontWeight:700, color:"#DC2626", background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:4, padding:"2px 7px" }}>LOW</span>}
      </div>
      <div style={{ fontSize:26, fontWeight:800, color, lineHeight:1 }}>
        {remaining.toLocaleString("en-IN")}
        <span style={{ fontSize:13, fontWeight:400, color:"#9CA3AF" }}> / {OWNER_POOL.toLocaleString("en-IN")}</span>
      </div>
      <div style={{ height:5, background:"#E5E7EB", borderRadius:99, marginTop:10, overflow:"hidden" }}>
        <div style={{ height:"100%", width:`${pct}%`, background:color, borderRadius:99 }} />
      </div>
      <div style={{ fontSize:11, color:"#9CA3AF", marginTop:5 }}>
        {issued.toLocaleString("en-IN")} consumed · {Math.round(pct)}% of pool used
      </div>
    </div>
  );
}

/* ── Confirm dialog ─────────────────────────────────────── */
function ConfirmDialog({ message, onConfirm, onCancel, danger }) {
  return (
    <div style={{
      position:"fixed", inset:0, zIndex:500,
      background:"rgba(17,24,39,0.55)",
      display:"flex", alignItems:"center", justifyContent:"center", padding:16,
    }}>
      <motion.div
        initial={{ scale:.96, opacity:0 }} animate={{ scale:1, opacity:1 }}
        style={{
          width:"100%", maxWidth:360,
          background:"#fff", borderRadius:14, overflow:"hidden",
          boxShadow:"0 24px 64px rgba(0,0,0,0.18)",
        }}
      >
        <div style={{ height:3, background: danger ? "#DC2626" : "#D97706" }} />
        <div style={{ padding:"22px 22px 20px" }}>
          <div style={{ fontSize:14, color:"#374151", lineHeight:1.6, marginBottom:20 }}>{message}</div>
          <div style={{ display:"flex", gap:8 }}>
            <button
              onClick={onCancel}
              style={{
                flex:1, height:38, borderRadius:8, border:"1px solid #E5E7EB",
                background:"#fff", cursor:"pointer", fontSize:13, color:"#374151",
              }}
            >Cancel</button>
            <button
              onClick={onConfirm}
              style={{
                flex:1, height:38, borderRadius:8, border:"none",
                background: danger ? "#DC2626" : "#D97706",
                cursor:"pointer", fontSize:13, fontWeight:600, color:"#fff",
              }}
            >Confirm</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/* ── Org detail modal ───────────────────────────────────── */
function OrgDetailModal({ org, onClose, onAction }) {
  const [actionLoading,   setActionLoading]   = useState(null);
  const [confirm,         setConfirm]         = useState(null);
  const [showInvoice,     setShowInvoice]     = useState(false);
  const [invoices,        setInvoices]        = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [grantCalls,      setGrantCalls]      = useState("");
  const [deductCalls,     setDeductCalls]     = useState("");

  const totalPaid = org.payments.reduce((s, p) => s + p.amount, 0);

  useEffect(() => {
    setInvoicesLoading(true);
    adminFetch(`/api/admin/invoices?orgId=${org.id}`)
      .then(rows => setInvoices(rows))
      .catch(() => {})
      .finally(() => setInvoicesLoading(false));
  }, [org.id]); // eslint-disable-line
  const pctUsed   = org.apiCallsTotal > 0 ? Math.round((org.apiCallsUsed / org.apiCallsTotal) * 100) : 0;
  const color     = pctUsed >= 90 ? "#DC2626" : pctUsed >= 70 ? "#D97706" : "#16A34A";

  async function doAction(type, payload) {
    setActionLoading(type);
    try {
      await onAction(org.id, type, payload);
    } finally {
      setActionLoading(null);
      setConfirm(null);
    }
  }

  return (
    <>
      <motion.div
        initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
        transition={{ duration:0.15 }}
        style={{
          position:"fixed", inset:0, zIndex:300,
          background:"rgba(17,24,39,0.5)",
          display:"flex", alignItems:"center", justifyContent:"center", padding:16,
        }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ scale:.96, y:16, opacity:0 }}
          animate={{ scale:1,   y:0,  opacity:1 }}
          exit={{    scale:.96, y:16, opacity:0 }}
          transition={{ duration:0.18, ease:"easeOut" }}
          style={{
            width:"100%", maxWidth:580,
            background:"#ffffff", border:"1px solid #E5E7EB",
            borderRadius:16, overflow:"hidden",
            boxShadow:"0 24px 64px rgba(0,0,0,0.14)",
            maxHeight:"90vh", display:"flex", flexDirection:"column",
          }}
        >
          {/* Top accent bar */}
          <div style={{ height:3, background: org.isSuspended ? "#DC2626" : org.couponCode ? "#D97706" : "#2563EB", flexShrink:0 }} />

          {/* Header */}
          <div style={{
            padding:"20px 24px 16px",
            borderBottom:"1px solid #F3F4F6",
            flexShrink:0,
            display:"flex", alignItems:"flex-start", justifyContent:"space-between",
          }}>
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                <span style={{ fontSize:17, fontWeight:700, color:"#111827" }}>{org.name}</span>
                {org.couponCode && (
                  <span style={{
                    fontSize:10, fontWeight:700, color:"#D97706",
                    background:"#FEF3C7", border:"1px solid #FDE68A",
                    borderRadius:5, padding:"2px 7px",
                  }}>COUPON: {org.couponCode}</span>
                )}
                {org.isSuspended && (
                  <span style={{
                    fontSize:10, fontWeight:700, color:"#DC2626",
                    background:"#FEF2F2", border:"1px solid #FECACA",
                    borderRadius:5, padding:"2px 7px",
                  }}>SUSPENDED</span>
                )}
              </div>
              <div style={{ fontSize:13, color:"#6B7280", marginTop:4 }}>
                {org.email} · Joined {fmtDate(org.createdAt)}
              </div>
            </div>
            <button onClick={onClose} style={{
              background:"none", border:"1px solid #E5E7EB", borderRadius:8,
              width:32, height:32, cursor:"pointer", color:"#9CA3AF",
              display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
            }}><X size={14} /></button>
          </div>

          <div style={{ flex:1, overflowY:"auto", padding:24 }}>
            {/* Admin actions */}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:11, fontWeight:600, color:"#6B7280", textTransform:"uppercase", letterSpacing:0.5, marginBottom:10 }}>
                Admin Actions
              </div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {org.isSuspended ? (
                  <button
                    onClick={() => setConfirm("unsuspend")}
                    disabled={!!actionLoading}
                    style={{
                      display:"flex", alignItems:"center", gap:6,
                      background:"#F0FDF4", border:"1px solid #BBF7D0",
                      borderRadius:8, padding:"8px 14px", cursor:"pointer",
                      fontSize:13, fontWeight:600, color:"#16A34A",
                    }}
                  >
                    {actionLoading === "unsuspend" ? <Loader2 size={13} style={{ animation:"spin 1s linear infinite" }} /> : <Shield size={13} />}
                    Restore Access
                  </button>
                ) : (
                  <button
                    onClick={() => setConfirm("suspend")}
                    disabled={!!actionLoading}
                    style={{
                      display:"flex", alignItems:"center", gap:6,
                      background:"#FEF2F2", border:"1px solid #FECACA",
                      borderRadius:8, padding:"8px 14px", cursor:"pointer",
                      fontSize:13, fontWeight:600, color:"#DC2626",
                    }}
                  >
                    {actionLoading === "suspend" ? <Loader2 size={13} style={{ animation:"spin 1s linear infinite" }} /> : <ShieldOff size={13} />}
                    Suspend Account
                  </button>
                )}

                {org.couponCode && totalPaid === 0 && (
                  <button
                    onClick={() => setConfirm("revoke")}
                    disabled={!!actionLoading}
                    style={{
                      display:"flex", alignItems:"center", gap:6,
                      background:"#FFFBEB", border:"1px solid #FDE68A",
                      borderRadius:8, padding:"8px 14px", cursor:"pointer",
                      fontSize:13, fontWeight:600, color:"#D97706",
                    }}
                  >
                    {actionLoading === "revoke" ? <Loader2 size={13} style={{ animation:"spin 1s linear infinite" }} /> : <CreditCard size={13} />}
                    Revoke Free Quota
                  </button>
                )}

                <button
                  onClick={() => setShowInvoice(true)}
                  style={{
                    display:"flex", alignItems:"center", gap:6,
                    background:"#0C1A2E", border:"1px solid #0C1A2E",
                    borderRadius:8, padding:"8px 14px", cursor:"pointer",
                    fontSize:13, fontWeight:600, color:"#fff",
                  }}
                >
                  <FileText size={13} />
                  Generate Invoice
                </button>
              </div>
            </div>

            {/* Quota & tier management */}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:11, fontWeight:600, color:"#6B7280", textTransform:"uppercase", letterSpacing:0.5, marginBottom:10 }}>
                Quota &amp; Product Tier
              </div>
              <div style={{ background:"#F9FAFB", border:"1px solid #F3F4F6", borderRadius:10, padding:"14px 16px", display:"flex", flexDirection:"column", gap:12 }}>
                <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <input
                      type="number" min="1" placeholder="Calls" value={grantCalls}
                      onChange={e => setGrantCalls(e.target.value)}
                      style={{ width:80, height:34, padding:"0 10px", border:"1.5px solid #E5E7EB", borderRadius:7, fontSize:13, color:"#111827" }}
                    />
                    <button
                      disabled={!!actionLoading || !grantCalls}
                      onClick={async () => {
                        const calls = parseInt(grantCalls, 10);
                        if (!calls || calls < 1) return;
                        await doAction("grant-quota", { calls });
                        setGrantCalls("");
                      }}
                      style={{
                        display:"flex", alignItems:"center", gap:6,
                        background:"#F0FDF4", border:"1px solid #BBF7D0",
                        borderRadius:8, padding:"8px 14px", cursor: grantCalls ? "pointer" : "default",
                        fontSize:13, fontWeight:600, color:"#16A34A", opacity: grantCalls ? 1 : 0.6,
                      }}
                    >
                      {actionLoading === "grant-quota" ? <Loader2 size={13} style={{ animation:"spin 1s linear infinite" }} /> : <UserPlus size={13} />}
                      Grant Calls
                    </button>
                  </div>

                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <input
                      type="number" min="1" placeholder="Calls" value={deductCalls}
                      onChange={e => setDeductCalls(e.target.value)}
                      style={{ width:80, height:34, padding:"0 10px", border:"1.5px solid #E5E7EB", borderRadius:7, fontSize:13, color:"#111827" }}
                    />
                    <button
                      disabled={!!actionLoading || !deductCalls}
                      onClick={() => setConfirm("deduct-quota")}
                      style={{
                        display:"flex", alignItems:"center", gap:6,
                        background:"#FEF2F2", border:"1px solid #FECACA",
                        borderRadius:8, padding:"8px 14px", cursor: deductCalls ? "pointer" : "default",
                        fontSize:13, fontWeight:600, color:"#DC2626", opacity: deductCalls ? 1 : 0.6,
                      }}
                    >
                      {actionLoading === "deduct-quota" ? <Loader2 size={13} style={{ animation:"spin 1s linear infinite" }} /> : <CreditCard size={13} />}
                      Deduct Calls
                    </button>
                  </div>
                </div>

                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:12, fontWeight:600, color:"#6B7280" }}>Product Tier</span>
                  <div style={{ display:"flex", border:"1.5px solid #E5E7EB", borderRadius:8, overflow:"hidden" }}>
                    {["tracking", "fleet"].map(tier => {
                      const active = (org.productTier || "tracking") === tier;
                      return (
                        <button
                          key={tier}
                          disabled={!!actionLoading || active}
                          onClick={() => setConfirm(`set-tier:${tier}`)}
                          style={{
                            border:"none", padding:"7px 14px", fontSize:12.5, fontWeight:600,
                            cursor: active ? "default" : "pointer",
                            background: active ? "#0C1A2E" : "#fff",
                            color: active ? "#fff" : "#6B7280",
                          }}
                        >
                          {tier === "fleet" ? "Fleet Management" : "Vehicle Tracking"}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Contact info */}
            {(org.phone || org.email) && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:11, fontWeight:600, color:"#6B7280", textTransform:"uppercase", letterSpacing:0.5, marginBottom:10 }}>Contact</div>
                <div style={{ background:"#F9FAFB", border:"1px solid #F3F4F6", borderRadius:10, padding:"12px 16px", display:"flex", flexDirection:"column", gap:6 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10, fontSize:13, color:"#374151" }}>
                    <span style={{ fontSize:11, fontWeight:600, color:"#9CA3AF", width:44, flexShrink:0 }}>Email</span>
                    <span>{org.email}</span>
                  </div>
                  {org.phone && (
                    <div style={{ display:"flex", alignItems:"center", gap:10, fontSize:13, color:"#374151" }}>
                      <span style={{ fontSize:11, fontWeight:600, color:"#9CA3AF", width:44, flexShrink:0 }}>Phone</span>
                      <span style={{ fontFamily:"monospace" }}>{org.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* API usage */}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:11, fontWeight:600, color:"#6B7280", textTransform:"uppercase", letterSpacing:0.5, marginBottom:10 }}>API Usage</div>
              <div style={{ background:"#F9FAFB", border:"1px solid #F3F4F6", borderRadius:10, padding:"14px 16px" }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:10 }}>
                  <span style={{ fontSize:13, color:"#374151" }}>
                    {org.apiCallsUsed.toLocaleString("en-IN")} used of {org.apiCallsTotal.toLocaleString("en-IN")}
                  </span>
                  <span style={{ fontSize:13, fontWeight:700, color }}>
                    {Math.max(0, org.apiCallsTotal - org.apiCallsUsed).toLocaleString("en-IN")} remaining
                  </span>
                </div>
                <div style={{ height:8, background:"#E5E7EB", borderRadius:99, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${pctUsed}%`, background:color, borderRadius:99, transition:"width 0.4s" }} />
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", marginTop:6 }}>
                  <span style={{ fontSize:11, color:"#9CA3AF" }}>{pctUsed}% consumed</span>
                  {org.couponCode && totalPaid === 0 && (
                    <span style={{ fontSize:11, color:"#D97706", fontWeight:600 }}>Free trial — no payment received</span>
                  )}
                </div>

                {/* Real call counts from audit log */}
                {(org.realCallsUsed > 0 || org.callsFailed > 0) && (
                  <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid #E5E7EB" }}>
                    <div style={{ fontSize:10, fontWeight:700, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:0.4, marginBottom:8 }}>
                      Actual Masters India Calls (from audit log)
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:8 }}>
                      <div style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:8, padding:"8px 10px" }}>
                        <div style={{ fontSize:16, fontWeight:800, color:"#111827" }}>{(org.realCallsUsed || 0).toLocaleString("en-IN")}</div>
                        <div style={{ fontSize:10, color:"#16A34A", fontWeight:600, marginTop:2 }}>Successful</div>
                      </div>
                      <div style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:8, padding:"8px 10px" }}>
                        <div style={{ fontSize:16, fontWeight:800, color: (org.callsFailed||0) > 0 ? "#DC2626" : "#9CA3AF" }}>{(org.callsFailed || 0).toLocaleString("en-IN")}</div>
                        <div style={{ fontSize:10, color: (org.callsFailed||0) > 0 ? "#DC2626" : "#9CA3AF", fontWeight:600, marginTop:2 }}>Failed</div>
                      </div>
                      <div style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:8, padding:"8px 10px" }}>
                        <div style={{ fontSize:16, fontWeight:800, color:"#7C3AED" }}>{(org.uniqueVehicles || 0).toLocaleString("en-IN")}</div>
                        <div style={{ fontSize:10, color:"#7C3AED", fontWeight:600, marginTop:2 }}>Unique Plates</div>
                      </div>
                      <div style={{
                        background: Math.abs((org.realCallsUsed||0) - org.apiCallsUsed) > 2 ? "#FEF2F2" : "#F0FDF4",
                        border:`1px solid ${Math.abs((org.realCallsUsed||0) - org.apiCallsUsed) > 2 ? "#FECACA" : "#BBF7D0"}`,
                        borderRadius:8, padding:"8px 10px",
                      }}>
                        <div style={{ fontSize:16, fontWeight:800, color: Math.abs((org.realCallsUsed||0) - org.apiCallsUsed) > 2 ? "#DC2626" : "#16A34A" }}>
                          {Math.abs((org.realCallsUsed||0) - org.apiCallsUsed)}
                        </div>
                        <div style={{ fontSize:10, color: Math.abs((org.realCallsUsed||0) - org.apiCallsUsed) > 2 ? "#DC2626" : "#16A34A", fontWeight:600, marginTop:2 }}>
                          {Math.abs((org.realCallsUsed||0) - org.apiCallsUsed) > 2 ? "Counter drift!" : "In sync"}
                        </div>
                      </div>
                    </div>
                    {org.lastCallAt && (
                      <div style={{ fontSize:11, color:"#9CA3AF", marginTop:8 }}>
                        Last call: {new Date(org.lastCallAt).toLocaleString("en-IN", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit", hour12:true })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Payment history */}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:11, fontWeight:600, color:"#6B7280", textTransform:"uppercase", letterSpacing:0.5, marginBottom:10 }}>
                Payment History · ₹{totalPaid.toLocaleString("en-IN")} total
              </div>
              {!org.payments.length ? (
                <div style={{
                  background: org.couponCode ? "#FFFBEB" : "#F9FAFB",
                  border:`1px solid ${org.couponCode ? "#FDE68A" : "#F3F4F6"}`,
                  borderRadius:10, padding:"16px", textAlign:"center",
                  fontSize:13, color: org.couponCode ? "#D97706" : "#9CA3AF",
                }}>
                  {org.couponCode ? "No payment received — registered with coupon code" : "No payments yet"}
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {org.payments.map(p => (
                    <div key={p.id} style={{
                      display:"flex", justifyContent:"space-between", alignItems:"center",
                      background:"#F9FAFB", border:"1px solid #F3F4F6",
                      borderRadius:10, padding:"10px 14px",
                    }}>
                      <div>
                        <div style={{ fontSize:13, fontWeight:600, color:"#111827" }}>₹{p.amount}</div>
                        <div style={{ fontSize:11, color:"#9CA3AF", marginTop:2 }}>
                          {p.apiCallsPurchased} API calls · {fmtTime(p.createdAt)}
                        </div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:11, color:"#16A34A", fontWeight:600 }}>✓ Paid</div>
                        <div style={{ fontSize:10, color:"#D1D5DB", marginTop:2, fontFamily:"monospace" }}>
                          {p.transactionId || "—"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Vehicles */}
            <div>
              <div style={{ fontSize:11, fontWeight:600, color:"#6B7280", textTransform:"uppercase", letterSpacing:0.5, marginBottom:10 }}>
                Registered Vehicles · {(org.vehicles || []).length}
              </div>
              {!(org.vehicles || []).length ? (
                <div style={{ background:"#F9FAFB", border:"1px solid #F3F4F6", borderRadius:10, padding:"16px", textAlign:"center", fontSize:13, color:"#9CA3AF" }}>
                  No vehicles added yet
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {org.vehicles.map(v => (
                    <div key={v.id} style={{
                      display:"flex", justifyContent:"space-between", alignItems:"center",
                      background:"#F9FAFB", border:"1px solid #F3F4F6",
                      borderRadius:8, padding:"8px 12px",
                    }}>
                      <div>
                        <span style={{ fontSize:13, fontWeight:600, color:"#111827" }}>{v.nickname}</span>
                        <span style={{ fontSize:11, color:"#9CA3AF", marginLeft:8 }}>{v.plateNumber} · {v.type}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Invoices */}
            <div style={{ marginTop:20 }}>
              <div style={{ fontSize:11, fontWeight:600, color:"#6B7280", textTransform:"uppercase", letterSpacing:0.5, marginBottom:10, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <span>Invoices · {invoices.length}</span>
                <button onClick={() => setShowInvoice(true)} style={{ display:"flex", alignItems:"center", gap:4, background:"#0C1A2E", border:"none", borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:11, fontWeight:600, color:"#fff" }}>
                  <FileText size={11} /> New Invoice
                </button>
              </div>
              {invoicesLoading ? (
                <div style={{ textAlign:"center", padding:16 }}><Loader2 size={16} style={{ animation:"spin 1s linear infinite", color:"#9CA3AF" }} /></div>
              ) : !invoices.length ? (
                <div style={{ background:"#F9FAFB", border:"1px solid #F3F4F6", borderRadius:10, padding:"16px", textAlign:"center", fontSize:13, color:"#9CA3AF" }}>
                  No invoices yet — click Generate Invoice to create one
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {invoices.map(inv => {
                    const statusStyle =
                      inv.status === "paid"      ? { bg:"#F0FDF4", color:"#16A34A", border:"#BBF7D0", label:"Paid" } :
                      inv.status === "cancelled" ? { bg:"#FEF2F2", color:"#DC2626", border:"#FECACA", label:"Cancelled" } :
                                                   { bg:"#EFF6FF", color:"#2563EB", border:"#BFDBFE", label:"Sent" };
                    return (
                      <div key={inv.id} style={{ background:"#F9FAFB", border:"1px solid #F3F4F6", borderRadius:10, padding:"12px 14px" }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                          <div>
                            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
                              <span style={{ fontFamily:"monospace", fontSize:12, fontWeight:700, color:"#111827" }}>{inv.invoice_number}</span>
                              <span style={{ fontSize:10, fontWeight:700, background:statusStyle.bg, color:statusStyle.color, border:`1px solid ${statusStyle.border}`, borderRadius:20, padding:"2px 8px" }}>{statusStyle.label}</span>
                            </div>
                            <div style={{ fontSize:12, color:"#6B7280" }}>{inv.description}</div>
                            <div style={{ fontSize:11, color:"#9CA3AF", marginTop:2 }}>{inv.calls_qty} calls · {fmtDate(inv.created_at)}</div>
                          </div>
                          <div style={{ textAlign:"right", flexShrink:0, marginLeft:12 }}>
                            <div style={{ fontSize:16, fontWeight:800, color:"#111827" }}>₹{Number(inv.total).toLocaleString("en-IN", { minimumFractionDigits:2 })}</div>
                            <div style={{ display:"flex", gap:6, marginTop:6, justifyContent:"flex-end" }}>
                              {inv.status !== "paid" && (
                                <button
                                  onClick={async () => {
                                    try { await adminFetch(`/api/admin/invoices/${inv.id}/status`, "PATCH", { status:"paid" }); setInvoices(p => p.map(i => i.id === inv.id ? { ...i, status:"paid" } : i)); } catch {}
                                  }}
                                  style={{ fontSize:10, fontWeight:600, background:"#F0FDF4", color:"#16A34A", border:"1px solid #BBF7D0", borderRadius:5, padding:"3px 8px", cursor:"pointer" }}
                                >Mark Paid</button>
                              )}
                              <button
                                onClick={async () => {
                                  try { await adminFetch(`/api/admin/invoices/${inv.id}/resend`, "POST"); } catch {}
                                }}
                                style={{ fontSize:10, fontWeight:600, background:"#EFF6FF", color:"#2563EB", border:"1px solid #BFDBFE", borderRadius:5, padding:"3px 8px", cursor:"pointer", display:"flex", alignItems:"center", gap:3 }}
                              ><RotateCcw size={9} /> Resend</button>
                              <button
                                onClick={async () => {
                                  try { await adminOpenPdf(`/api/admin/invoices/${inv.id}/pdf`); } catch {}
                                }}
                                style={{ fontSize:10, fontWeight:600, background:"#0C1A2E", color:"#fff", border:"none", borderRadius:5, padding:"3px 8px", cursor:"pointer", display:"flex", alignItems:"center", gap:3 }}
                              ><FileText size={9} /> View PDF</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Confirm dialog */}
      <AnimatePresence>
        {confirm === "suspend" && (
          <ConfirmDialog
            message={`Suspend ${org.name}? They will immediately lose all API access and see a suspension notice.`}
            onConfirm={() => doAction("suspend")}
            onCancel={() => setConfirm(null)}
            danger
          />
        )}
        {confirm === "unsuspend" && (
          <ConfirmDialog
            message={`Restore access for ${org.name}? They will be able to use their remaining API quota immediately.`}
            onConfirm={() => doAction("unsuspend")}
            onCancel={() => setConfirm(null)}
          />
        )}
        {confirm === "revoke" && (
          <ConfirmDialog
            message={`Revoke free quota for ${org.name}? Their remaining free calls will be zeroed out — they must pay to continue.`}
            onConfirm={() => doAction("revoke")}
            onCancel={() => setConfirm(null)}
            danger
          />
        )}
        {confirm === "deduct-quota" && (
          <ConfirmDialog
            message={`Deduct ${deductCalls} calls from ${org.name}? Their remaining balance will drop by this amount (never below what they've already used).`}
            onConfirm={async () => {
              const calls = parseInt(deductCalls, 10);
              if (!calls || calls < 1) return setConfirm(null);
              await doAction("deduct-quota", { calls });
              setDeductCalls("");
            }}
            onCancel={() => setConfirm(null)}
            danger
          />
        )}
        {confirm?.startsWith("set-tier:") && (
          <ConfirmDialog
            message={`Move ${org.name} to ${confirm.endsWith("fleet") ? "Fleet Management" : "Vehicle Tracking"}? This changes which features and dashboards they can access immediately.`}
            onConfirm={() => doAction("set-tier", { tier: confirm.split(":")[1] })}
            onCancel={() => setConfirm(null)}
          />
        )}
      </AnimatePresence>

      {/* Invoice modal */}
      <AnimatePresence>
        {showInvoice && (
          <InvoiceModal
            org={org}
            onClose={() => setShowInvoice(false)}
            onCreated={inv => setInvoices(p => [inv, ...p])}
          />
        )}
      </AnimatePresence>
    </>
  );
}

/* ── Invoice Modal ───────────────────────────────────────── */
function InvoiceModal({ org, onClose, onCreated }) {
  const inp = {
    width: "100%", padding: "9px 12px", borderRadius: 8,
    border: "1px solid #E5E7EB", fontSize: 13, color: "#111827",
    outline: "none", background: "#fff",
  };
  const label = { fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4, display: "block", marginBottom: 5 };

  const defaultCalls = org.apiCallsTotal || 200;
  const defaultRate  = defaultCalls >= 200 ? 4.50 : defaultCalls >= 150 ? 5.00 : defaultCalls >= 100 ? 5.50 : 6.00;

  const [form, setForm] = useState({
    description:        `LorryKhojo Pro Plan — FASTag API Calls`,
    callsQty:           defaultCalls,
    ratePerCall:        defaultRate,
    taxType:            "IGST",
    paymentUpi:         "",
    paymentBankAccount: "",
    paymentIfsc:        "",
    paymentAccountName: "",
    notes:              "Your account is already active. Payment confirms the service.\nUnused calls do not expire. Top-up available at ₹3.75/call + GST.",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone]       = useState(null); // { invoice, emailSent }
  const [err, setErr]         = useState("");

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  const subtotal  = parseFloat(((form.callsQty || 0) * (form.ratePerCall || 0)).toFixed(2));
  const taxAmount = parseFloat((subtotal * 0.18).toFixed(2));
  const total     = parseFloat((subtotal + taxAmount).toFixed(2));

  async function submit() {
    setErr(""); setLoading(true);
    try {
      const res = await adminFetch("/api/admin/invoices", "POST", {
        orgId:              org.id,
        description:        form.description,
        callsQty:           parseInt(form.callsQty, 10),
        ratePerCall:        parseFloat(form.ratePerCall),
        taxType:            form.taxType,
        paymentUpi:         form.paymentUpi,
        paymentBankAccount: form.paymentBankAccount,
        paymentIfsc:        form.paymentIfsc,
        paymentAccountName: form.paymentAccountName,
        notes:              form.notes,
      });
      setDone(res);
      onCreated?.(res.invoice);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: "fixed", inset: 0, zIndex: 400,
        background: "rgba(17,24,39,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ scale: .96, y: 16, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: .96, y: 16, opacity: 0 }}
        transition={{ duration: 0.18, ease: "easeOut" }}
        style={{
          width: "100%", maxWidth: 540,
          background: "#fff", borderRadius: 16,
          border: "1px solid #E5E7EB",
          boxShadow: "0 24px 64px rgba(0,0,0,0.16)",
          maxHeight: "92vh", display: "flex", flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{ height: 3, background: "#0C1A2E", flexShrink: 0 }} />
        <div style={{ padding: "18px 24px 14px", borderBottom: "1px solid #F3F4F6", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <FileText size={16} color="#0C1A2E" />
              <span style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>Generate Invoice</span>
            </div>
            <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 3 }}>Will be emailed to <strong style={{ color: "#374151" }}>{org.email}</strong></div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 8, width: 30, height: 30, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#9CA3AF" }}><X size={13} /></button>
        </div>

        {done ? (
          /* Success state */
          <div style={{ padding: 32, textAlign: "center", flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#F0FDF4", border: "2px solid #BBF7D0", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CheckCircle size={28} color="#16A34A" />
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111827" }}>Invoice Sent!</div>
            <div style={{ fontSize: 13, color: "#6B7280", maxWidth: 320 }}>
              <strong>{done.invoice.invoice_number}</strong> has been created and {done.emailSent ? "emailed to " + org.email : "stored — email delivery failed, check SMTP config"}.
            </div>
            {!done.emailSent && done.emailError && (
              <div style={{ fontSize: 11, color: "#DC2626", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "8px 12px", maxWidth: 340 }}>
                Email error: {done.emailError}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button onClick={onClose} style={{ background: "#0C1A2E", color: "#fff", border: "none", borderRadius: 8, padding: "10px 24px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Done</button>
            </div>
          </div>
        ) : (
          /* Form */
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

            {err && (
              <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#DC2626", marginBottom: 16 }}>
                {err}
              </div>
            )}

            {/* Service row */}
            <div style={{ marginBottom: 16 }}>
              <label style={label}>Description</label>
              <input style={inp} value={form.description} onChange={e => set("description", e.target.value)} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div>
                <label style={label}>API Calls</label>
                <input style={inp} type="number" min="1" value={form.callsQty} onChange={e => set("callsQty", e.target.value)} />
              </div>
              <div>
                <label style={label}>Rate / Call (₹)</label>
                <input style={inp} type="number" min="0.01" step="0.01" value={form.ratePerCall} onChange={e => set("ratePerCall", e.target.value)} />
              </div>
              <div>
                <label style={label}>Tax Type</label>
                <select style={{ ...inp, cursor: "pointer" }} value={form.taxType} onChange={e => set("taxType", e.target.value)}>
                  <option value="IGST">IGST 18% (inter-state)</option>
                  <option value="CGST_SGST">CGST+SGST 9%+9%</option>
                </select>
              </div>
            </div>

            {/* Amount summary */}
            <div style={{ background: "#0C1A2E", borderRadius: 10, padding: "14px 18px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", gap: 24 }}>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: "rgba(255,255,255,0.38)", marginBottom: 3 }}>Subtotal</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>₹{subtotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
                </div>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: "rgba(255,255,255,0.38)", marginBottom: 3 }}>Tax</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>₹{taxAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: "rgba(255,255,255,0.38)", marginBottom: 3 }}>Total</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#F59E0B", letterSpacing: -0.5 }}>₹{total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
              </div>
            </div>

            {/* Payment details */}
            <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Payment Details (shown in invoice)</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <label style={label}>UPI ID</label>
                <input style={inp} placeholder="yourname@upi" value={form.paymentUpi} onChange={e => set("paymentUpi", e.target.value)} />
              </div>
              <div>
                <label style={label}>Account Holder Name</label>
                <input style={inp} placeholder="LorryKhojo Technologies" value={form.paymentAccountName} onChange={e => set("paymentAccountName", e.target.value)} />
              </div>
              <div>
                <label style={label}>Bank Account No.</label>
                <input style={inp} placeholder="XXXXXXXXXXXXXXXXXX" value={form.paymentBankAccount} onChange={e => set("paymentBankAccount", e.target.value)} />
              </div>
              <div>
                <label style={label}>IFSC Code</label>
                <input style={inp} placeholder="HDFC0001234" value={form.paymentIfsc} onChange={e => set("paymentIfsc", e.target.value)} />
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 20 }}>
              <label style={label}>Notes (shown in invoice)</label>
              <textarea style={{ ...inp, resize: "vertical", minHeight: 72, lineHeight: 1.6 }} value={form.notes} onChange={e => set("notes", e.target.value)} />
            </div>

            <button
              onClick={submit}
              disabled={loading || !form.description || !form.callsQty || !form.ratePerCall}
              style={{
                width: "100%", padding: "12px", borderRadius: 10, border: "none",
                background: loading ? "#6B7280" : "#0C1A2E", color: "#fff",
                fontSize: 14, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              {loading
                ? <><Loader2 size={15} style={{ animation: "spin 1s linear infinite" }} /> Generating &amp; Sending…</>
                : <><Send size={15} /> Generate &amp; Send Invoice to {org.email}</>}
            </button>

          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

/* ── Audit Logs view ─────────────────────────────────────── */
/* ── Excel/CSV export — no library needed ───────────────────── */
function exportToExcel(rows, filename) {
  const headers = [
    "S.No",
    "Date & Time (IST)",
    "Org Name",
    "Email",
    "Phone",
    "Endpoint",
    "Vehicle No.",
    "Status",
    "IP Address",
    "Duration (ms)",
    "Quota Used",
    "Quota Total",
    "Calls Remaining",
    "Registered Vehicles",
  ];

  const csvRows = rows.map((l, idx) => [
    idx + 1,
    l.called_at_ist ? new Date(l.called_at_ist).toLocaleString("en-IN", { timeZone:"Asia/Kolkata", hour12:true }) : "",
    l.org_name   || "",
    l.org_email  || "",
    l.org_phone  || "",
    l.endpoint   || "",
    l.vehicle_no || "",
    l.status     || "",
    l.ip_address || "",
    l.duration_ms != null ? l.duration_ms : "",
    l.org_calls_used  != null ? l.org_calls_used  : "",
    l.org_calls_total != null ? l.org_calls_total : "",
    (l.org_calls_total != null && l.org_calls_used != null)
      ? Math.max(0, l.org_calls_total - l.org_calls_used)
      : "",
    l.org_vehicle_count != null ? l.org_vehicle_count : "",
  ]);

  const escape = v => `"${String(v).replace(/"/g, '""')}"`;
  const csv    = [headers, ...csvRows].map(r => r.map(escape).join(",")).join("\r\n");
  const bom    = "﻿"; // UTF-8 BOM so Excel reads Indian chars correctly
  const blob   = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
  const url    = URL.createObjectURL(blob);
  const a      = document.createElement("a");
  a.href       = url;
  a.download   = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function AuditLogsView() {
  const [logs,        setLogs]        = useState([]);
  const [summary,     setSummary]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  const [from,        setFrom]        = useState("");
  const [to,          setTo]          = useState("");
  const [orgFilter,   setOrgFilter]   = useState("");
  const [statusFilter,setStatusFilter]= useState("all");
  const [tierFilter,  setTierFilter]  = useState("all");

  const loadLogs = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams({ limit: 5000 });
      if (from)                   params.set("from",   new Date(from).toISOString());
      if (to)                     params.set("to",     new Date(to + "T23:59:59").toISOString());
      if (statusFilter !== "all") params.set("status", statusFilter);
      const data = await adminFetch(`/api/admin/call-logs?${params}`);
      setLogs(Array.isArray(data) ? data : (data.logs || []));
      setSummary(data.summary || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [from, to, statusFilter]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const filtered = logs
    .filter(l => tierFilter === "all" || (l.product_tier || "tracking") === tierFilter)
    .filter(l =>
      !orgFilter ||
      (l.org_name   || "").toLowerCase().includes(orgFilter.toLowerCase()) ||
      (l.org_email  || "").toLowerCase().includes(orgFilter.toLowerCase()) ||
      (l.org_phone  || "").includes(orgFilter) ||
      (l.vehicle_no || "").toLowerCase().includes(orgFilter.toLowerCase()) ||
      (l.ip_address || "").includes(orgFilter)
    );

  const inputStyle = {
    height:34, padding:"0 11px", background:"#F9FAFB",
    border:"1px solid #E5E7EB", borderRadius:7, fontSize:12,
    color:"#111827", outline:"none", boxSizing:"border-box",
  };

  const successCount   = filtered.filter(l => l.status === "success").length;
  const failCount      = filtered.filter(l => l.status !== "success").length;
  const uniqueVehicles = new Set(filtered.map(l => l.vehicle_no).filter(Boolean)).size;
  const uniqueOrgs     = new Set(filtered.map(l => l.org_id).filter(Boolean)).size;
  const avgMs          = successCount > 0
    ? Math.round(filtered.filter(l => l.status === "success").reduce((s, l) => s + (l.duration_ms || 0), 0) / successCount)
    : null;

  const today = new Date().toISOString().slice(0, 10);

  const COLS = "1.4fr 1.8fr 1.1fr 0.6fr 0.9fr 1fr 0.8fr 0.7fr 0.5fr";
  const HEADERS = ["Time (IST)", "Organization", "Vehicle No.", "Endpoint", "Status", "IP Address", "Quota Used", "Vehicles", "ms"];

  return (
    <div>
      {/* Header row */}
      <div style={{ marginBottom:20, display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:16 }}>
        <div>
          <div style={{ fontSize:22, fontWeight:700, color:"#111827" }}>Audit Log</div>
          <div style={{ fontSize:14, color:"#6B7280", marginTop:4 }}>
            Full Masters India API call history — every request, every user, every vehicle
          </div>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
            <Filter size={12} color="#9CA3AF" />
            <span style={{ fontSize:11, color:"#9CA3AF", fontWeight:600 }}>FILTERS</span>
          </div>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} />
          <input type="date" value={to}   onChange={e => setTo(e.target.value)}   style={inputStyle} />
          <select
            value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ ...inputStyle, padding:"0 8px", cursor:"pointer" }}
          >
            <option value="all">All statuses</option>
            <option value="success">Success only</option>
            <option value="error">Errors only</option>
          </select>
          <select
            value={tierFilter} onChange={e => setTierFilter(e.target.value)}
            style={{ ...inputStyle, padding:"0 8px", cursor:"pointer" }}
          >
            <option value="all">All products</option>
            <option value="fleet">Fleet Management</option>
            <option value="tracking">Vehicle Tracking</option>
          </select>
          <input
            value={orgFilter} onChange={e => setOrgFilter(e.target.value)}
            placeholder="Search name / vehicle / IP / phone…"
            style={{ ...inputStyle, width:220 }}
            onFocus={e => e.target.style.borderColor="#2563EB"}
            onBlur={e  => e.target.style.borderColor="#E5E7EB"}
          />
          <button
            onClick={loadLogs}
            style={{ display:"flex", alignItems:"center", gap:5, background:"#111827", border:"none", borderRadius:7, padding:"0 14px", height:34, cursor:"pointer", fontSize:12, fontWeight:600, color:"#fff" }}
          >
            <RefreshCw size={12} style={loading ? { animation:"spin 1s linear infinite" } : {}} />
            Refresh
          </button>
          {filtered.length > 0 && (
            <button
              onClick={() => exportToExcel(filtered, `lorrykhojo-audit-${today}.csv`)}
              style={{ display:"flex", alignItems:"center", gap:5, background:"#16A34A", border:"none", borderRadius:7, padding:"0 14px", height:34, cursor:"pointer", fontSize:12, fontWeight:600, color:"#fff" }}
            >
              <svg width="13" height="13" viewBox="0 0 20 20" fill="none">
                <path d="M10 2v10M6 8l4 4 4-4M3 14v2a2 2 0 002 2h10a2 2 0 002-2v-2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Export Excel ({filtered.length})
            </button>
          )}
        </div>
      </div>

      {/* Summary chips */}
      {!loading && !error && (
        <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
          <div style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:8, padding:"8px 14px", display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:"#6B7280" }} />
            <span style={{ fontSize:13, fontWeight:700, color:"#111827" }}>{filtered.length}</span>
            <span style={{ fontSize:12, color:"#6B7280" }}>total calls</span>
          </div>
          <div style={{ background:"#F0FDF4", border:"1px solid #BBF7D0", borderRadius:8, padding:"8px 14px", display:"flex", alignItems:"center", gap:6 }}>
            <CheckCircle size={13} color="#16A34A" />
            <span style={{ fontSize:13, fontWeight:700, color:"#16A34A" }}>{successCount}</span>
            <span style={{ fontSize:12, color:"#15803D" }}>successful</span>
          </div>
          {failCount > 0 && (
            <div style={{ background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:8, padding:"8px 14px", display:"flex", alignItems:"center", gap:6 }}>
              <AlertCircle size={13} color="#DC2626" />
              <span style={{ fontSize:13, fontWeight:700, color:"#DC2626" }}>{failCount}</span>
              <span style={{ fontSize:12, color:"#B91C1C" }}>failed</span>
            </div>
          )}
          {uniqueVehicles > 0 && (
            <div style={{ background:"#F5F3FF", border:"1px solid #DDD6FE", borderRadius:8, padding:"8px 14px", display:"flex", alignItems:"center", gap:6 }}>
              <TrendingUp size={13} color="#7C3AED" />
              <span style={{ fontSize:13, fontWeight:700, color:"#7C3AED" }}>{uniqueVehicles}</span>
              <span style={{ fontSize:12, color:"#6D28D9" }}>unique vehicles</span>
            </div>
          )}
          {uniqueOrgs > 0 && (
            <div style={{ background:"#EFF6FF", border:"1px solid #BFDBFE", borderRadius:8, padding:"8px 14px", display:"flex", alignItems:"center", gap:6 }}>
              <Users size={13} color="#2563EB" />
              <span style={{ fontSize:13, fontWeight:700, color:"#2563EB" }}>{uniqueOrgs}</span>
              <span style={{ fontSize:12, color:"#1D4ED8" }}>organizations</span>
            </div>
          )}
          {avgMs != null && (
            <div style={{ background:"#FFFBEB", border:"1px solid #FDE68A", borderRadius:8, padding:"8px 14px", display:"flex", alignItems:"center", gap:6 }}>
              <Zap size={13} color="#D97706" />
              <span style={{ fontSize:13, fontWeight:700, color:"#D97706" }}>{avgMs}ms</span>
              <span style={{ fontSize:12, color:"#B45309" }}>avg response</span>
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:10, padding:"12px 16px", marginBottom:20, fontSize:13, color:"#DC2626", display:"flex", alignItems:"center", gap:8 }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {/* Per-org summary breakdown */}
      {!loading && !error && summary.length > 0 && (
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:11, fontWeight:700, color:"#6B7280", textTransform:"uppercase", letterSpacing:0.5, marginBottom:10 }}>
            Per-Organization Summary (all time)
          </div>
          <div style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:12, overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
            <div style={{ display:"grid", gridTemplateColumns:"2fr 1.5fr 0.8fr 0.8fr 0.7fr 0.7fr 0.9fr 1.4fr", padding:"8px 16px", background:"#F9FAFB", borderBottom:"1px solid #F3F4F6" }}>
              {["Organization","Contact","Total","Success","Failed","Plates","Avg ms","Last Call (IST)"].map(h => (
                <span key={h} style={{ fontSize:10, fontWeight:700, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:0.3 }}>{h}</span>
              ))}
            </div>
            {summary.map((s, i) => (
              <div key={s.org_id} style={{ display:"grid", gridTemplateColumns:"2fr 1.5fr 0.8fr 0.8fr 0.7fr 0.7fr 0.9fr 1.4fr", padding:"10px 16px", alignItems:"center", borderBottom: i < summary.length-1 ? "1px solid #F9FAFB" : "none", background: parseInt(s.failed_calls,10) > 0 ? "#FFFCF2" : "transparent" }}
                onMouseOver={e => e.currentTarget.style.background="#F9FAFB"}
                onMouseOut={e  => e.currentTarget.style.background = parseInt(s.failed_calls,10) > 0 ? "#FFFCF2" : "transparent"}
              >
                <div style={{ overflow:"hidden" }}>
                  <div style={{ fontSize:12, fontWeight:600, color:"#111827", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.org_name}</div>
                  <div style={{ fontSize:10, color:"#9CA3AF", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.org_email}</div>
                </div>
                <div style={{ fontSize:11, color:"#6B7280", fontFamily:"monospace", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {s.org_phone || <span style={{ color:"#D1D5DB" }}>—</span>}
                </div>
                <div style={{ fontSize:13, fontWeight:700, color:"#111827" }}>{parseInt(s.total_calls,10)}</div>
                <div style={{ fontSize:12, fontWeight:600, color:"#16A34A" }}>{parseInt(s.success_calls,10)}</div>
                <div style={{ fontSize:12, fontWeight:600, color: parseInt(s.failed_calls,10) > 0 ? "#DC2626" : "#9CA3AF" }}>
                  {parseInt(s.failed_calls,10) > 0 ? parseInt(s.failed_calls,10) : "—"}
                </div>
                <div style={{ fontSize:12, color:"#7C3AED", fontWeight:600 }}>
                  {parseInt(s.unique_vehicles,10) > 0 ? parseInt(s.unique_vehicles,10) : <span style={{ color:"#9CA3AF" }}>—</span>}
                </div>
                <div style={{ fontSize:11, color:"#6B7280" }}>
                  {s.avg_ms != null ? `${s.avg_ms}ms` : <span style={{ color:"#D1D5DB" }}>—</span>}
                </div>
                <div style={{ fontSize:10, color:"#6B7280" }}>
                  {s.last_call_at ? fmtTime(s.last_call_at) : <span style={{ color:"#D1D5DB" }}>—</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full detail log table */}
      <div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
          <div style={{ fontSize:11, fontWeight:700, color:"#6B7280", textTransform:"uppercase", letterSpacing:0.5 }}>
            Detailed Call Log — {filtered.length} records
          </div>
          {filtered.length > 0 && (
            <button
              onClick={() => exportToExcel(filtered, `lorrykhojo-audit-${today}.csv`)}
              style={{ display:"flex", alignItems:"center", gap:5, background:"none", border:"1px solid #16A34A", borderRadius:6, padding:"4px 12px", cursor:"pointer", fontSize:11, fontWeight:600, color:"#16A34A" }}
            >
              <svg width="11" height="11" viewBox="0 0 20 20" fill="none">
                <path d="M10 2v10M6 8l4 4 4-4M3 14v2a2 2 0 002 2h10a2 2 0 002-2v-2" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Export to Excel
            </button>
          )}
        </div>

        <div style={{ background:"#ffffff", border:"1px solid #E5E7EB", borderRadius:12, overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
          {/* Scrollable table wrapper */}
          <div style={{ overflowX:"auto" }}>
            {/* Column headers */}
            <div style={{ display:"grid", gridTemplateColumns:COLS, padding:"9px 18px", background:"#F9FAFB", borderBottom:"1px solid #F3F4F6", minWidth:900 }}>
              {HEADERS.map(h => (
                <span key={h} style={{ fontSize:10, fontWeight:700, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:0.4, whiteSpace:"nowrap" }}>{h}</span>
              ))}
            </div>

            {loading && (
              <div style={{ padding:"40px", textAlign:"center", color:"#9CA3AF", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                <Loader2 size={16} style={{ animation:"spin 1s linear infinite" }} /> Loading…
              </div>
            )}

            {!loading && filtered.length === 0 && (
              <div style={{ padding:"48px", textAlign:"center", color:"#9CA3AF", fontSize:14 }}>
                No API calls found for the selected filters.
              </div>
            )}

            {!loading && filtered.map((log, i) => {
              const isErr     = log.status !== "success";
              const remaining = (log.org_calls_total != null && log.org_calls_used != null)
                ? Math.max(0, log.org_calls_total - log.org_calls_used)
                : null;
              return (
                <div
                  key={i}
                  style={{
                    display:"grid", gridTemplateColumns:COLS,
                    padding:"10px 18px", minWidth:900,
                    borderBottom: i < filtered.length-1 ? "1px solid #F9FAFB" : "none",
                    alignItems:"center",
                    background: isErr ? "#FFFCF2" : "transparent",
                  }}
                  onMouseOver={e => e.currentTarget.style.background="#F0F4FF"}
                  onMouseOut={e  => e.currentTarget.style.background = isErr ? "#FFFCF2" : "transparent"}
                >
                  {/* Time */}
                  <div style={{ fontSize:11, color:"#374151" }}>
                    {fmtTime(log.called_at_ist)}
                  </div>

                  {/* Organization */}
                  <div style={{ overflow:"hidden" }}>
                    <div style={{ fontSize:12, fontWeight:600, color:"#111827", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{log.org_name}</div>
                    <div style={{ fontSize:10, color:"#9CA3AF", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {log.org_email}
                      {log.org_phone && <span style={{ marginLeft:5, color:"#D1D5DB" }}>· {log.org_phone}</span>}
                    </div>
                  </div>

                  {/* Vehicle No */}
                  <div style={{ fontSize:11, fontWeight:700, color:"#111827", fontFamily:"monospace", letterSpacing:0.5 }}>
                    {log.vehicle_no || <span style={{ color:"#D1D5DB", fontFamily:"inherit", fontWeight:400 }}>—</span>}
                  </div>

                  {/* Endpoint */}
                  <div style={{ fontSize:10, color:"#6B7280", background:"#F3F4F6", borderRadius:4, padding:"2px 6px", fontFamily:"monospace", whiteSpace:"nowrap", display:"inline-block" }}>
                    {log.endpoint}
                  </div>

                  {/* Status */}
                  <div>
                    <span style={{
                      fontSize:10, fontWeight:600, padding:"2px 7px", borderRadius:99,
                      background: isErr ? "#FEF2F2" : "#F0FDF4",
                      color:      isErr ? "#DC2626" : "#16A34A",
                      border:     `1px solid ${isErr ? "#FECACA" : "#BBF7D0"}`,
                      whiteSpace:"nowrap",
                    }}>{log.status === "success" ? "✓ success" : log.status}</span>
                  </div>

                  {/* IP Address */}
                  <div style={{ fontSize:10, color:"#6B7280", fontFamily:"monospace", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {log.ip_address
                      ? (log.ip_address === "::1" || log.ip_address === "127.0.0.1"
                          ? <span style={{ color:"#9CA3AF" }}>localhost</span>
                          : log.ip_address)
                      : <span style={{ color:"#D1D5DB" }}>—</span>}
                  </div>

                  {/* Quota used / total */}
                  <div>
                    {log.org_calls_total != null ? (
                      <>
                        <div style={{ fontSize:11, color:"#374151" }}>
                          <span style={{ fontWeight:600 }}>{log.org_calls_used}</span>
                          <span style={{ color:"#9CA3AF" }}> / {log.org_calls_total}</span>
                        </div>
                        <div style={{ fontSize:9, color: remaining === 0 ? "#DC2626" : remaining < 10 ? "#D97706" : "#9CA3AF" }}>
                          {remaining} left
                        </div>
                      </>
                    ) : <span style={{ color:"#D1D5DB", fontSize:11 }}>—</span>}
                  </div>

                  {/* Registered vehicles */}
                  <div style={{ fontSize:12, fontWeight:600, color: log.org_vehicle_count > 0 ? "#7C3AED" : "#9CA3AF" }}>
                    {log.org_vehicle_count != null ? log.org_vehicle_count : "—"}
                  </div>

                  {/* Duration ms */}
                  <div style={{ fontSize:11, color: log.duration_ms > 5000 ? "#DC2626" : "#9CA3AF" }}>
                    {log.duration_ms != null ? `${log.duration_ms}ms` : "—"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Payment Audit View ──────────────────────────────────── */
function exportPaymentsCSV(rows, filename) {
  const headers = [
    "S.No", "Date & Time (IST)", "Org Name", "Email", "Phone",
    "Base Amount (₹)", "GST 18% (₹)", "Total Paid (₹)",
    "API Calls", "Plan", "Status",
    "PhonePe Order ID", "UTR / Transaction ID", "Gateway", "Invoice Sent",
  ];
  const data = rows.map((p, i) => [
    i + 1,
    new Date(p.created_at).toLocaleString("en-IN", { timeZone:"Asia/Kolkata", hour12:true }),
    p.org_name  || "",
    p.org_email || "",
    p.org_phone || "",
    p.amount    || 0,
    Math.round((p.amount || 0) * 0.18),
    Math.round((p.amount || 0) * 1.18),
    p.calls_added || 0,
    p.rate_type   || "",
    p.status      || "",
    p.merchant_order_id || p.razorpay_order_id || "",
    p.transaction_id    || p.razorpay_payment_id || "",
    p.gateway || "phonepe",
    p.receipt_sent ? "Yes" : "No",
  ]);
  const escape = v => `"${String(v).replace(/"/g, '""')}"`;
  const csv  = [headers, ...data].map(r => r.map(escape).join(",")).join("\r\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const PLAN_LABELS = {
  plan_50:  "Starter — 50 calls",
  plan_100: "Standard — 100 calls",
  plan_150: "Growth — 150 calls",
  plan_200: "Pro — 200 calls",
  topup:    "Top-up",
};

function PaymentAuditView() {
  const [payments,     setPayments]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");
  const [from,         setFrom]         = useState("");
  const [to,           setTo]           = useState("");
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tierFilter,   setTierFilter]   = useState("all");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const data = await adminFetch("/api/admin/payments");
      setPayments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = payments.filter(p => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (tierFilter !== "all" && (p.product_tier || "tracking") !== tierFilter) return false;
    if (from && new Date(p.created_at) < new Date(from)) return false;
    if (to   && new Date(p.created_at) > new Date(to + "T23:59:59")) return false;
    if (search) {
      const q = search.toLowerCase();
      return (p.org_name           || "").toLowerCase().includes(q)
          || (p.org_email          || "").toLowerCase().includes(q)
          || (p.org_phone          || "").includes(q)
          || (p.merchant_order_id  || "").toLowerCase().includes(q)
          || (p.transaction_id     || "").toLowerCase().includes(q)
          || (p.razorpay_order_id  || "").toLowerCase().includes(q)
          || (p.razorpay_payment_id|| "").toLowerCase().includes(q);
    }
    return true;
  });

  const successRows  = filtered.filter(p => p.status === "success");
  const failedCount  = filtered.filter(p => p.status === "failed").length;
  const pendingCount = filtered.filter(p => p.status === "pending").length;
  const totalRevBase = successRows.reduce((s, p) => s + (p.amount || 0), 0);
  const totalRevGST  = Math.round(totalRevBase * 1.18);

  const today = new Date().toISOString().slice(0, 10);
  const inputStyle = {
    height: 34, padding: "0 11px", background: "#F9FAFB",
    border: "1px solid #E5E7EB", borderRadius: 7, fontSize: 12,
    color: "#111827", outline: "none", boxSizing: "border-box",
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 20, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>Transaction Audit Log</div>
          <div style={{ fontSize: 14, color: "#6B7280", marginTop: 4 }}>
            Every payment attempt — successful, failed, and pending
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <Filter size={12} color="#9CA3AF" />
            <span style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 600 }}>FILTERS</span>
          </div>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} />
          <input type="date" value={to}   onChange={e => setTo(e.target.value)}   style={inputStyle} />
          <select
            value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ ...inputStyle, padding: "0 8px", cursor: "pointer" }}
          >
            <option value="all">All statuses</option>
            <option value="success">Successful</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
          <select
            value={tierFilter} onChange={e => setTierFilter(e.target.value)}
            style={{ ...inputStyle, padding: "0 8px", cursor: "pointer" }}
          >
            <option value="all">All products</option>
            <option value="fleet">Fleet Management</option>
            <option value="tracking">Vehicle Tracking</option>
          </select>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search name / email / UTR / order ID…"
            style={{ ...inputStyle, width: 250 }}
            onFocus={e => e.target.style.borderColor = "#5f259f"}
            onBlur={e  => e.target.style.borderColor = "#E5E7EB"}
          />
          <button
            onClick={load}
            style={{ display: "flex", alignItems: "center", gap: 5, background: "#111827", border: "none", borderRadius: 7, padding: "0 14px", height: 34, cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#fff" }}
          >
            <RefreshCw size={12} style={loading ? { animation: "spin 1s linear infinite" } : {}} />
            Refresh
          </button>
          {filtered.length > 0 && (
            <button
              onClick={() => exportPaymentsCSV(filtered, `lorrykhojo-transactions-${today}.csv`)}
              style={{ display: "flex", alignItems: "center", gap: 5, background: "#16A34A", border: "none", borderRadius: 7, padding: "0 14px", height: 34, cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#fff" }}
            >
              <svg width="13" height="13" viewBox="0 0 20 20" fill="none">
                <path d="M10 2v10M6 8l4 4 4-4M3 14v2a2 2 0 002 2h10a2 2 0 002-2v-2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Export CSV ({filtered.length})
            </button>
          )}
        </div>
      </div>

      {/* Summary chips */}
      {!loading && !error && (
        <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
          <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 16px" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#111827" }}>₹{totalRevBase.toLocaleString("en-IN")}</div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>base revenue · ₹{totalRevGST.toLocaleString("en-IN")} incl. GST</div>
          </div>
          <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, padding: "8px 16px", display: "flex", alignItems: "center", gap: 8 }}>
            <CheckCircle size={14} color="#16A34A" />
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#16A34A" }}>{successRows.length}</div>
              <div style={{ fontSize: 11, color: "#15803D" }}>successful</div>
            </div>
          </div>
          {failedCount > 0 && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "8px 16px", display: "flex", alignItems: "center", gap: 8 }}>
              <AlertCircle size={14} color="#DC2626" />
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#DC2626" }}>{failedCount}</div>
                <div style={{ fontSize: 11, color: "#B91C1C" }}>failed</div>
              </div>
            </div>
          )}
          {pendingCount > 0 && (
            <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "8px 16px", display: "flex", alignItems: "center", gap: 8 }}>
              <AlertTriangle size={14} color="#D97706" />
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#D97706" }}>{pendingCount}</div>
                <div style={{ fontSize: 11, color: "#B45309" }}>pending</div>
              </div>
            </div>
          )}
          <div style={{ background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 8, padding: "8px 16px" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#7C3AED" }}>{successRows.reduce((s, p) => s + (p.calls_added || 0), 0).toLocaleString("en-IN")}</div>
            <div style={{ fontSize: 11, color: "#6D28D9" }}>total calls credited</div>
          </div>
        </div>
      )}

      {error && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#DC2626", display: "flex", alignItems: "center", gap: 8 }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {/* Table */}
      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
        <div style={{ overflowX: "auto" }}>
          {/* Column headers */}
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1.9fr 0.9fr 0.8fr 0.7fr 0.7fr 2.4fr 2.4fr", padding: "9px 18px", background: "#F9FAFB", borderBottom: "1px solid #F3F4F6", minWidth: 1060 }}>
            {["Date & Time (IST)", "Organization", "Base (₹)", "Total + GST", "Calls", "Status", "PhonePe Order ID", "UTR / Transaction ID"].map(h => (
              <span key={h} style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap" }}>{h}</span>
            ))}
          </div>

          {loading && (
            <div style={{ padding: "40px", textAlign: "center", color: "#9CA3AF", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> Loading…
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div style={{ padding: "48px", textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>
              No transactions found for the selected filters.
            </div>
          )}

          {!loading && filtered.map((p, i) => {
            const isSuccess = p.status === "success";
            const isFailed  = p.status === "failed";
            const isPending = p.status === "pending";

            const statusStyle = isSuccess
              ? { bg: "#F0FDF4", color: "#16A34A", border: "#BBF7D0", label: "✓ Success" }
              : isFailed
              ? { bg: "#FEF2F2", color: "#DC2626", border: "#FECACA", label: "✗ Failed" }
              : { bg: "#FFFBEB", color: "#D97706", border: "#FDE68A", label: "⏳ Pending" };

            const baseAmt  = p.amount || 0;
            const totalAmt = Math.round(baseAmt * 1.18);
            const utr      = p.transaction_id || p.razorpay_payment_id || null;
            const orderId  = p.merchant_order_id || p.razorpay_order_id || null;

            return (
              <div
                key={p.id}
                style={{
                  display: "grid", gridTemplateColumns: "1.4fr 1.9fr 0.9fr 0.8fr 0.7fr 0.7fr 2.4fr 2.4fr",
                  padding: "11px 18px", minWidth: 1060,
                  borderBottom: i < filtered.length - 1 ? "1px solid #F9FAFB" : "none",
                  alignItems: "center",
                  background: isFailed ? "#FFFCF2" : "transparent",
                }}
                onMouseOver={e => e.currentTarget.style.background = "#F0F4FF"}
                onMouseOut={e  => e.currentTarget.style.background = isFailed ? "#FFFCF2" : "transparent"}
              >
                {/* Date/time */}
                <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.5 }}>
                  <div>{new Date(p.created_at).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric", timeZone:"Asia/Kolkata" })}</div>
                  <div style={{ color: "#9CA3AF" }}>{new Date(p.created_at).toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit", second:"2-digit", hour12:true, timeZone:"Asia/Kolkata" })}</div>
                </div>

                {/* Organization */}
                <div style={{ overflow: "hidden" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.org_name || "—"}</div>
                  <div style={{ fontSize: 10, color: "#9CA3AF", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.org_email}
                    {p.org_phone && <span style={{ marginLeft: 5 }}>· {p.org_phone}</span>}
                  </div>
                </div>

                {/* Base amount */}
                <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                  ₹{baseAmt.toLocaleString("en-IN")}
                  {baseAmt > 0 && (
                    <div style={{ fontSize: 9, color: "#9CA3AF", fontWeight: 400 }}>+₹{Math.round(baseAmt * 0.18)} GST</div>
                  )}
                </div>

                {/* Total with GST */}
                <div style={{ fontSize: 13, fontWeight: 800, color: isSuccess ? "#16A34A" : "#374151" }}>
                  ₹{totalAmt.toLocaleString("en-IN")}
                </div>

                {/* Calls + plan */}
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#7C3AED" }}>{(p.calls_added || 0).toLocaleString("en-IN")}</div>
                  <div style={{ fontSize: 9, color: "#9CA3AF" }}>{PLAN_LABELS[p.rate_type] || p.rate_type || ""}</div>
                </div>

                {/* Status badge */}
                <div>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 99,
                    background: statusStyle.bg, color: statusStyle.color,
                    border: `1px solid ${statusStyle.border}`,
                    whiteSpace: "nowrap",
                  }}>{statusStyle.label}</span>
                  {p.receipt_sent && isSuccess && (
                    <div style={{ fontSize: 9, color: "#16A34A", marginTop: 3 }}>Invoice sent</div>
                  )}
                </div>

                {/* PhonePe Order ID */}
                <div style={{ overflow: "hidden" }}>
                  {orderId ? (
                    <span
                      onClick={() => navigator.clipboard?.writeText(orderId)}
                      title="Click to copy"
                      style={{ fontSize: 10, fontFamily: "monospace", color: "#374151", cursor: "pointer", wordBreak: "break-all" }}
                    >{orderId}</span>
                  ) : (
                    <span style={{ color: "#D1D5DB", fontSize: 11 }}>—</span>
                  )}
                </div>

                {/* UTR / Transaction ID */}
                <div style={{ overflow: "hidden" }}>
                  {utr ? (
                    <span
                      onClick={() => navigator.clipboard?.writeText(utr)}
                      title="Click to copy"
                      style={{ fontSize: 10, fontFamily: "monospace", color: isSuccess ? "#059669" : "#374151", fontWeight: isSuccess ? 700 : 400, cursor: "pointer", wordBreak: "break-all" }}
                    >{utr}</span>
                  ) : (
                    <span style={{ color: isPending ? "#D97706" : "#D1D5DB", fontSize: 11 }}>
                      {isPending ? "Awaiting confirmation" : "—"}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {filtered.length > 0 && (
        <div style={{ marginTop: 12, fontSize: 12, color: "#9CA3AF", textAlign: "right" }}>
          Showing {filtered.length} of {payments.length} transactions · Click any Order ID or UTR to copy
        </div>
      )}
    </div>
  );
}

/* ── Account Deletions audit view ──────────────────────────── */
function AccountDeletionsView() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [search,  setSearch]  = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const data = await adminFetch("/api/admin/account-deletions");
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.org_name  || "").toLowerCase().includes(q)
        || (r.org_email || "").toLowerCase().includes(q)
        || (r.ip_address || "").includes(q);
  });

  const inputStyle = {
    height: 34, padding: "0 11px", background: "#F9FAFB",
    border: "1px solid #E5E7EB", borderRadius: 7, fontSize: 12,
    color: "#111827", outline: "none", boxSizing: "border-box",
  };

  return (
    <div>
      <div style={{ marginBottom: 20, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>Account Deletions</div>
          <div style={{ fontSize: 14, color: "#6B7280", marginTop: 4 }}>
            Audit trail of every self-service and admin-initiated account deletion
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search name / email / IP…"
            style={{ ...inputStyle, width: 250 }}
          />
          <button
            onClick={load}
            style={{ display: "flex", alignItems: "center", gap: 5, background: "#111827", border: "none", borderRadius: 7, padding: "0 14px", height: 34, cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#fff" }}
          >
            <RefreshCw size={12} style={loading ? { animation: "spin 1s linear infinite" } : {}} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#DC2626", display: "flex", alignItems: "center", gap: 8 }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
        <div style={{ overflowX: "auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1.8fr 2fr 0.8fr 1.2fr", padding: "9px 18px", background: "#F9FAFB", borderBottom: "1px solid #F3F4F6", minWidth: 780 }}>
            {["Date & Time (IST)", "Organization", "Email at deletion", "Deleted by", "IP Address"].map(h => (
              <span key={h} style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap" }}>{h}</span>
            ))}
          </div>

          {loading && (
            <div style={{ padding: "40px", textAlign: "center", color: "#9CA3AF", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> Loading…
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div style={{ padding: "48px", textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>
              No account deletions recorded.
            </div>
          )}

          {!loading && filtered.map((r, i) => (
            <div
              key={r.id}
              style={{
                display: "grid", gridTemplateColumns: "1.4fr 1.8fr 2fr 0.8fr 1.2fr",
                padding: "11px 18px", minWidth: 780,
                borderBottom: i < filtered.length - 1 ? "1px solid #F9FAFB" : "none",
                alignItems: "center",
              }}
              onMouseOver={e => e.currentTarget.style.background = "#F0F4FF"}
              onMouseOut={e  => e.currentTarget.style.background = "transparent"}
            >
              <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.5 }}>
                <div>{new Date(r.deleted_at).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric", timeZone:"Asia/Kolkata" })}</div>
                <div style={{ color: "#9CA3AF" }}>{new Date(r.deleted_at).toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit", second:"2-digit", hour12:true, timeZone:"Asia/Kolkata" })}</div>
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.org_name}</div>
              <div style={{ fontSize: 11, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.org_email}</div>
              <div>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 99,
                  background: r.deleted_by === "self" ? "#EFF6FF" : "#FEF2F2",
                  color:      r.deleted_by === "self" ? "#2563EB" : "#DC2626",
                  border: `1px solid ${r.deleted_by === "self" ? "#BFDBFE" : "#FECACA"}`,
                }}>{r.deleted_by}</span>
              </div>
              <div style={{ fontSize: 11, fontFamily: "monospace", color: "#9CA3AF" }}>{r.ip_address || "—"}</div>
            </div>
          ))}
        </div>
      </div>

      {filtered.length > 0 && (
        <div style={{ marginTop: 12, fontSize: 12, color: "#9CA3AF", textAlign: "right" }}>
          Showing {filtered.length} of {rows.length} deletions
        </div>
      )}
    </div>
  );
}

/* ── Create Org Modal ────────────────────────────────────── */
function CreateOrgModal({ onClose, onCreated }) {
  const [name,     setName]     = useState("");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [phone,    setPhone]    = useState("");
  const [calls,    setCalls]    = useState(100);
  const [showPw,   setShowPw]   = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [err,      setErr]      = useState("");

  const inp = {
    height:38, padding:"0 12px", background:"#F9FAFB",
    border:"1px solid #E5E7EB", borderRadius:8, fontSize:13,
    color:"#111827", outline:"none", width:"100%", boxSizing:"border-box",
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setErr("");
    if (!name.trim())  return setErr("Name is required");
    if (!email.trim()) return setErr("Email is required");
    if (!password.trim() || password.length < 6) return setErr("Password must be at least 6 characters");
    setSaving(true);
    try {
      const res = await adminFetch("/api/admin/orgs/create", "POST", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        phone: phone.trim() || null,
        calls,
      });
      onCreated(res.org);
      onClose();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{ position:"fixed", inset:0, zIndex:9999, background:"rgba(17,24,39,0.55)", display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ scale:0.95, opacity:0, y:12 }}
        animate={{ scale:1, opacity:1, y:0 }}
        exit={{ scale:0.95, opacity:0, y:12 }}
        transition={{ duration:0.2 }}
        style={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:440, boxShadow:"0 24px 64px rgba(0,0,0,0.15)", overflow:"hidden" }}
      >
        {/* Header */}
        <div style={{ height:4, background:"#4F46E5" }} />
        <div style={{ padding:"20px 24px 16px", borderBottom:"1px solid #F3F4F6", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <div style={{ fontSize:17, fontWeight:700, color:"#111827" }}>Create Account</div>
            <div style={{ fontSize:12, color:"#6B7280", marginTop:2 }}>New org — no payment needed, calls granted immediately</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"1px solid #E5E7EB", borderRadius:8, width:32, height:32, cursor:"pointer", color:"#9CA3AF", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <X size={14} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding:"20px 24px 24px" }}>
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

            {/* Name */}
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:"#6B7280", textTransform:"uppercase", letterSpacing:0.4, display:"block", marginBottom:5 }}>Company / Org Name *</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Sharma Logistics" style={inp}
                onFocus={e => e.target.style.borderColor="#4F46E5"} onBlur={e => e.target.style.borderColor="#E5E7EB"} />
            </div>

            {/* Email */}
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:"#6B7280", textTransform:"uppercase", letterSpacing:0.4, display:"block", marginBottom:5 }}>Email *</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="owner@company.com" style={inp}
                onFocus={e => e.target.style.borderColor="#4F46E5"} onBlur={e => e.target.style.borderColor="#E5E7EB"} />
            </div>

            {/* Password */}
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:"#6B7280", textTransform:"uppercase", letterSpacing:0.4, display:"block", marginBottom:5 }}>Password *</label>
              <div style={{ position:"relative" }}>
                <input type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Min 6 characters" style={{ ...inp, paddingRight:40 }}
                  onFocus={e => e.target.style.borderColor="#4F46E5"} onBlur={e => e.target.style.borderColor="#E5E7EB"} />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"#9CA3AF", padding:0, display:"flex" }}>
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {/* Phone (optional) */}
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:"#6B7280", textTransform:"uppercase", letterSpacing:0.4, display:"block", marginBottom:5 }}>Phone <span style={{ fontWeight:400, textTransform:"none" }}>(optional)</span></label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="9876543210" style={inp}
                onFocus={e => e.target.style.borderColor="#4F46E5"} onBlur={e => e.target.style.borderColor="#E5E7EB"} />
            </div>

            {/* Free Calls */}
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:"#6B7280", textTransform:"uppercase", letterSpacing:0.4, display:"block", marginBottom:5 }}>Free API Calls to Grant</label>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:8 }}>
                {[0, 50, 100, 200, 500].map(n => (
                  <button key={n} type="button" onClick={() => setCalls(n)}
                    style={{ padding:"4px 12px", borderRadius:99, fontSize:12, fontWeight:600, cursor:"pointer", border:`1.5px solid ${calls===n?"#4F46E5":"#E5E7EB"}`, background:calls===n?"#EEF2FF":"#F9FAFB", color:calls===n?"#4F46E5":"#374151" }}>
                    {n === 0 ? "No calls" : `${n} calls`}
                  </button>
                ))}
              </div>
              <input type="number" min={0} max={10000} value={calls} onChange={e => setCalls(Math.max(0, parseInt(e.target.value,10)||0))}
                style={{ ...inp, width:140 }}
                onFocus={e => e.target.style.borderColor="#4F46E5"} onBlur={e => e.target.style.borderColor="#E5E7EB"} />
            </div>
          </div>

          {err && (
            <div style={{ marginTop:14, background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:8, padding:"10px 14px", fontSize:13, color:"#DC2626", display:"flex", alignItems:"center", gap:6 }}>
              <AlertCircle size={14} /> {err}
            </div>
          )}

          {/* Summary */}
          <div style={{ marginTop:16, background:"#F9FAFB", border:"1px solid #F3F4F6", borderRadius:10, padding:"12px 16px", fontSize:12, color:"#374151", lineHeight:1.7 }}>
            <div><strong>Email:</strong> {email || "—"}</div>
            <div><strong>Free calls:</strong> {calls > 0 ? `${calls} (has_paid = true)` : "0 (must pay to track)"}</div>
          </div>

          <button
            type="submit"
            disabled={saving}
            style={{ width:"100%", marginTop:18, height:44, borderRadius:10, background:saving?"#9CA3AF":"#4F46E5", border:"none", color:"#fff", fontSize:14, fontWeight:700, cursor:saving?"default":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}
          >
            {saving && <Loader2 size={15} style={{ animation:"spin 1s linear infinite" }} />}
            {saving ? "Creating…" : `Create Account${calls > 0 ? ` · ${calls} free calls` : ""}`}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

/* ── Admin login ─────────────────────────────────────────── */
function AdminLogin({ onAuth }) {
  const [u,       setU]       = useState("");
  const [p,       setP]       = useState("");
  const [show,    setShow]    = useState(false);
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: u, password: p }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Login failed");
      sessionStorage.setItem("trackin_admin_token", data.token);
      onAuth();
    } catch (err) {
      setError(err.message || "Invalid credentials. Access denied.");
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight:"100vh", background:"#0F172A", display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <motion.div
        initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
        transition={{ duration:0.22 }}
        style={{ width:"100%", maxWidth:380, background:"#ffffff", borderRadius:14, overflow:"hidden", boxShadow:"0 32px 80px rgba(0,0,0,0.5)" }}
      >
        <div style={{ background:"#0F172A", padding:"18px 24px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:32, height:32, borderRadius:8, background:"#2563EB", display:"flex", alignItems:"center", justifyContent:"center" }}>
              {/* Truck icon */}
              <svg width="18" height="14" viewBox="0 0 22 16" fill="none">
                <rect x="0" y="3" width="13" height="9" rx="1.5" fill="#fff"/>
                <path d="M13 5h5l3 4v4h-8V5z" fill="rgba(255,255,255,0.85)"/>
                <circle cx="4"  cy="14" r="2" fill="#2563EB" stroke="#fff" strokeWidth="1"/>
                <circle cx="17" cy="14" r="2" fill="#2563EB" stroke="#fff" strokeWidth="1"/>
              </svg>
            </div>
            <span style={{ fontSize:14, fontWeight:800, color:"#fff", letterSpacing:-0.3 }}>LorryKhojo</span>
          </div>
          <span style={{ fontSize:9, fontWeight:800, letterSpacing:0.8, color:"#EF4444", background:"rgba(239,68,68,0.12)", border:"1px solid rgba(239,68,68,0.25)", borderRadius:4, padding:"3px 8px" }}>ADMIN</span>
        </div>
        <div style={{ padding:"28px 28px 32px" }}>
          <div style={{ marginBottom:22 }}>
            <div style={{ fontSize:18, fontWeight:700, color:"#111827" }}>Admin Portal</div>
            <div style={{ fontSize:12, color:"#9CA3AF", marginTop:4 }}>LorryKhojo internal — authorized personnel only.</div>
          </div>
          {error && (
            <div style={{ background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:8, padding:"10px 14px", marginBottom:16, fontSize:13, color:"#DC2626" }}>{error}</div>
          )}
          <form onSubmit={handleSubmit} style={{ display:"flex", flexDirection:"column", gap:16 }}>
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:6, letterSpacing:0.4, textTransform:"uppercase" }}>Username</label>
              <input
                value={u} onChange={e => { setU(e.target.value); setError(""); }}
                placeholder="Enter admin username" autoComplete="username"
                style={{ width:"100%", boxSizing:"border-box", height:42, padding:"0 12px", background:"#F9FAFB", border:"1.5px solid #E5E7EB", borderRadius:8, fontSize:14, color:"#111827", outline:"none" }}
                onFocus={e => e.target.style.borderColor="#111827"}
                onBlur={e  => e.target.style.borderColor="#E5E7EB"}
              />
            </div>
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:6, letterSpacing:0.4, textTransform:"uppercase" }}>Password</label>
              <div style={{ position:"relative" }}>
                <input
                  value={p} onChange={e => { setP(e.target.value); setError(""); }}
                  type={show ? "text" : "password"} placeholder="Enter admin password" autoComplete="current-password"
                  style={{ width:"100%", boxSizing:"border-box", height:42, padding:"0 40px 0 12px", background:"#F9FAFB", border:"1.5px solid #E5E7EB", borderRadius:8, fontSize:14, color:"#111827", outline:"none" }}
                  onFocus={e => e.target.style.borderColor="#111827"}
                  onBlur={e  => e.target.style.borderColor="#E5E7EB"}
                />
                <button type="button" onClick={() => setShow(s => !s)} style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"#9CA3AF", display:"flex" }}>
                  {show ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <button
              type="submit" disabled={loading}
              style={{ width:"100%", height:44, marginTop:4, background: loading ? "#1F2937" : "#111827", border:"none", borderRadius:8, color:"#fff", fontSize:14, fontWeight:600, cursor: loading ? "default" : "pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}
            >
              {loading && <Loader2 size={15} style={{ animation:"spin 1s linear infinite" }} />}
              {loading ? "Verifying…" : "Access Admin Panel →"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}

/* ── Admin dashboard ─────────────────────────────────────── */
function AdminDashboard({ onLogout }) {
  const [view,          setView]          = useState("orgs"); // orgs | logs
  const [orgs,          setOrgs]          = useState([]);
  const [poolUsed,      setPoolUsed]      = useState(0);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState("");
  const [selectedOrg,   setSelectedOrg]   = useState(null);
  const [filterText,    setFilterText]    = useState("");
  const [activeTab,     setActiveTab]     = useState("all"); // all | coupon | suspended
  const [actionMsg,     setActionMsg]     = useState("");
  const [showCreateOrg, setShowCreateOrg] = useState(false);

  // Enable normal page scroll — global CSS sets overflow:hidden on html+body for the map page.
  // Must add class to <html> (documentElement) because the CSS rule targets html.page-scrollable.
  useEffect(() => {
    document.documentElement.classList.add("page-scrollable");
    return () => document.documentElement.classList.remove("page-scrollable");
  }, []);

  const loadOrgs = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const data = await adminFetch("/api/admin/orgs");
      // Response is now { orgs: [...], poolUsed: N }
      setOrgs(Array.isArray(data) ? data : (data.orgs || []));
      setPoolUsed(typeof data.poolUsed === "number" ? data.poolUsed : 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  async function handleAction(orgId, type, payload) {
    const pathMap = {
      suspend:        `/api/admin/orgs/${orgId}/suspend`,
      unsuspend:      `/api/admin/orgs/${orgId}/unsuspend`,
      revoke:         `/api/admin/orgs/${orgId}/revoke-quota`,
      "grant-quota":  `/api/admin/orgs/${orgId}/grant-quota`,
      "deduct-quota": `/api/admin/orgs/${orgId}/deduct-quota`,
      "set-tier":     `/api/admin/orgs/${orgId}/set-tier`,
    };
    await adminFetch(pathMap[type], "POST", payload);
    setActionMsg(
      type === "suspend"       ? "Account suspended." :
      type === "unsuspend"     ? "Access restored." :
      type === "revoke"        ? "Free quota revoked — user must pay to continue." :
      type === "grant-quota"   ? `${payload.calls} API calls granted.` :
      type === "deduct-quota"  ? `${payload.calls} API calls deducted.` :
      type === "set-tier"      ? `Product tier set to ${payload.tier === "fleet" ? "Fleet Management" : "Vehicle Tracking"}.` :
                                  ""
    );
    setTimeout(() => setActionMsg(""), 4000);
    // Refresh data and update the selected org state too
    const freshData = await adminFetch("/api/admin/orgs");
    const freshOrgs = Array.isArray(freshData) ? freshData : (freshData.orgs || []);
    setOrgs(freshOrgs);
    if (typeof freshData.poolUsed === "number") setPoolUsed(freshData.poolUsed);
    if (selectedOrg) {
      const updated = freshOrgs.find(o => o.id === selectedOrg.id);
      if (updated) setSelectedOrg(updated);
    }
  }

  // Free access = hasPaid=true but zero actual payments (coupon or admin-granted)
  const freeAccessOrgs = orgs.filter(o => o.hasPaid && o.payments.length === 0);
  const couponOrgs     = orgs.filter(o => o.couponCode);
  const suspendedOrgs  = orgs.filter(o => o.isSuspended);
  const activeOrgs     = orgs.filter(o => o.hasPaid && !o.isSuspended);
  const noQuotaOrgs    = orgs.filter(o => !o.hasPaid);
  const fleetOrgs      = orgs.filter(o => o.productTier === "fleet");
  const totalRevenue   = orgs.reduce((s, o) => s + o.payments.reduce((ps, p) => ps + p.amount, 0), 0);
  const totalIssued    = orgs.reduce((s, o) => s + o.apiCallsTotal, 0);
  const totalUsed      = orgs.reduce((s, o) => s + o.apiCallsUsed, 0);
  const totalVehicles  = orgs.reduce((s, o) => s + (o.vehicles?.length || 0), 0);

  const displayOrgs = orgs.filter(o => {
    if (activeTab === "free")      return o.hasPaid && o.payments.length === 0;
    if (activeTab === "coupon")    return !!o.couponCode;
    if (activeTab === "suspended") return o.isSuspended;
    if (activeTab === "fleet")     return o.productTier === "fleet";
    return true;
  }).filter(o =>
    !filterText || [o.name, o.email, o.couponCode || ""].some(s => s.toLowerCase().includes(filterText.toLowerCase()))
  );

  return (
    <div style={{ minHeight:"100vh", background:"#F7F8FA" }}>
      {/* Nav */}
      <header style={{ height:56, background:"#111827", display:"flex", alignItems:"center", padding:"0 24px", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:28, height:28, borderRadius:7, background:"#2563EB", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <svg width="16" height="12" viewBox="0 0 22 16" fill="none">
              <rect x="0" y="2" width="13" height="9" rx="1.5" fill="#fff"/>
              <path d="M13 4h5l3 4v4h-8V4z" fill="rgba(255,255,255,0.85)"/>
              <circle cx="4"  cy="13" r="2" fill="#2563EB" stroke="#fff" strokeWidth="1"/>
              <circle cx="17" cy="13" r="2" fill="#2563EB" stroke="#fff" strokeWidth="1"/>
            </svg>
          </div>
          <span style={{ fontSize:14, fontWeight:800, color:"#fff", letterSpacing:-0.3 }}>LorryKhojo</span>
          <span style={{ fontSize:10, fontWeight:600, color:"#9CA3AF", background:"rgba(255,255,255,0.1)", padding:"2px 8px", borderRadius:4 }}>Admin Portal</span>

          {/* View switcher tabs */}
          <div style={{ marginLeft:16, display:"flex", gap:2, background:"rgba(255,255,255,0.06)", borderRadius:8, padding:3 }}>
            {[
              { key:"orgs",      label:"Organizations",     Icon:Users      },
              { key:"payments",  label:"Transactions",      Icon:CreditCard },
              { key:"logs",      label:"Audit Logs",        Icon:Activity   },
              { key:"deletions", label:"Account Deletions", Icon:UserX      },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setView(key)}
                style={{
                  display:"flex", alignItems:"center", gap:5,
                  padding:"4px 12px", borderRadius:6, border:"none", cursor:"pointer",
                  fontSize:12, fontWeight:600,
                  background: view === key ? "#fff"               : "none",
                  color:      view === key ? "#111827"            : "rgba(255,255,255,0.55)",
                  transition:"all 0.15s",
                }}
              >
                <Icon size={12} />{label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          {view === "orgs" && (
            <>
              <button
                onClick={() => setShowCreateOrg(true)}
                style={{ display:"flex", alignItems:"center", gap:5, background:"#4F46E5", border:"none", borderRadius:7, padding:"0 14px", height:32, cursor:"pointer", fontSize:12, fontWeight:600, color:"#fff" }}
              >
                <UserPlus size={12} /> Create Account
              </button>
              <button
                onClick={loadOrgs}
                style={{ display:"flex", alignItems:"center", gap:5, background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:7, padding:"0 12px", height:32, cursor:"pointer", fontSize:12, color:"#D1D5DB" }}
              >
                <RefreshCw size={12} style={loading ? { animation:"spin 1s linear infinite" } : {}} /> Refresh
              </button>
            </>
          )}
          <button
            onClick={onLogout}
            style={{ display:"flex", alignItems:"center", gap:6, background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:7, padding:"0 12px", height:32, cursor:"pointer", fontSize:12, color:"#D1D5DB" }}
          ><LogOut size={12} /> Sign Out</button>
        </div>
      </header>

      {/* Action feedback toast */}
      <AnimatePresence>
        {actionMsg && (
          <motion.div
            initial={{ opacity:0, y:-20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-20 }}
            style={{
              position:"fixed", top:70, left:"50%", transform:"translateX(-50%)",
              background:"#111827", color:"#fff", borderRadius:8,
              padding:"10px 18px", fontSize:13, fontWeight:500,
              zIndex:9999, display:"flex", alignItems:"center", gap:8,
              boxShadow:"0 4px 16px rgba(0,0,0,0.2)",
            }}
          >
            <CheckCircle size={14} color="#10B981" />{actionMsg}
          </motion.div>
        )}
      </AnimatePresence>

      <div style={{ maxWidth:1140, margin:"0 auto", padding:"28px 24px" }}>

        {/* ── Audit Logs view ── */}
        {view === "logs" && <AuditLogsView />}

        {/* ── Transaction audit view ── */}
        {view === "payments" && <PaymentAuditView />}

        {/* ── Account deletions audit view ── */}
        {view === "deletions" && <AccountDeletionsView />}

        {/* ── Organizations view ── */}
        {view === "orgs" && (<>
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:22, fontWeight:700, color:"#111827" }}>Organization Overview</div>
          <div style={{ fontSize:14, color:"#6B7280", marginTop:4 }}>{orgs.length} registered organizations</div>
        </div>

        {error && (
          <div style={{ background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:10, padding:"12px 16px", marginBottom:20, fontSize:13, color:"#DC2626", display:"flex", alignItems:"center", gap:8 }}>
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {/* Stats row 1 — 3 cards + owner pool */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:14, marginBottom:14 }}>
          <StatCard
            label="Total Organizations" value={orgs.length}
            sub={`${activeOrgs.length} active · ${noQuotaOrgs.length} no quota`}
            icon={Users}
          />
          <StatCard
            label="Active Users (Has Quota)"
            value={activeOrgs.length}
            sub={`${freeAccessOrgs.length} free access · ${orgs.filter(o=>o.payments.length>0).length} paid`}
            icon={Shield} color="#2563EB" accent="#2563EB"
          />
          <StatCard
            label="Total Revenue"
            value={`₹${totalRevenue.toLocaleString("en-IN")}`}
            sub={`${orgs.reduce((s,o)=>s+o.payments.length,0)} payment${orgs.reduce((s,o)=>s+o.payments.length,0)!==1?"s":""}`}
            icon={DollarSign} color="#16A34A" accent="#16A34A"
          />
          <StatCard
            label="Total Vehicles Tracked"
            value={totalVehicles}
            sub={`across ${activeOrgs.length} active org${activeOrgs.length!==1?"s":""}`}
            icon={TrendingUp} color="#7C3AED" accent="#7C3AED"
          />
        </div>

        {/* Owner API pool — actual calls consumed vs Masters India 10k */}
        <div style={{ marginBottom:14 }}>
          <OwnerPoolBar issued={poolUsed} />
        </div>

        {/* Stats row 2 — API pool */}
        <div style={{
          background:"#ffffff", border:"1px solid #E5E7EB",
          borderRadius:12, padding:"18px 20px", marginBottom:28,
          boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
        }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:32, height:32, borderRadius:8, background:"#EFF6FF", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <Zap size={15} color="#2563EB" />
              </div>
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:"#111827" }}>API Usage Across All Orgs</div>
                <div style={{ fontSize:11, color:"#9CA3AF" }}>Real-time quota consumption</div>
              </div>
            </div>
            <div style={{ textAlign:"right" }}>
              <div style={{ fontSize:20, fontWeight:800, color: totalIssued>0 && totalUsed/totalIssued>0.9 ? "#DC2626" : "#111827" }}>
                {totalUsed.toLocaleString("en-IN")}
                <span style={{ fontSize:13, fontWeight:400, color:"#9CA3AF" }}> / {totalIssued.toLocaleString("en-IN")} used</span>
              </div>
              <div style={{ fontSize:12, color:"#9CA3AF" }}>
                {Math.max(0, totalIssued - totalUsed).toLocaleString("en-IN")} calls remaining across all orgs
              </div>
            </div>
          </div>
          <div style={{ height:8, background:"#E5E7EB", borderRadius:99, overflow:"hidden" }}>
            <div style={{
              height:"100%",
              width: totalIssued > 0 ? `${Math.min((totalUsed/totalIssued)*100,100)}%` : "0%",
              background: totalIssued > 0 && totalUsed/totalIssued > 0.9 ? "#DC2626" : totalUsed/totalIssued > 0.7 ? "#D97706" : "#2563EB",
              borderRadius:99, transition:"width 0.4s",
            }} />
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", marginTop:8 }}>
            <div style={{ display:"flex", gap:16 }}>
              {activeOrgs.map(o => {
                const left = Math.max(0, o.apiCallsTotal - o.apiCallsUsed);
                const pct  = o.apiCallsTotal > 0 ? Math.round((o.apiCallsUsed/o.apiCallsTotal)*100) : 0;
                return (
                  <div key={o.id} style={{ display:"flex", alignItems:"center", gap:5 }}>
                    <div style={{
                      width:6, height:6, borderRadius:"50%",
                      background: pct>=90 ? "#DC2626" : pct>=70 ? "#D97706" : "#10B981",
                    }} />
                    <span style={{ fontSize:11, color:"#6B7280" }}>
                      {o.name}: <strong style={{ color:"#374151" }}>{o.apiCallsUsed}/{o.apiCallsTotal}</strong>
                      <span style={{ color:"#9CA3AF" }}> ({left} left)</span>
                    </span>
                  </div>
                );
              })}
            </div>
            <span style={{ fontSize:11, color:"#9CA3AF" }}>
              {totalIssued > 0 ? Math.round((totalUsed/totalIssued)*100) : 0}% of issued quota consumed
            </span>
          </div>
        </div>

        {/* Tabs + table */}
        <div style={{ background:"#ffffff", border:"1px solid #E5E7EB", borderRadius:12, overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
          {/* Tab bar + search */}
          <div style={{ padding:"14px 18px", borderBottom:"1px solid #F3F4F6", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
            <div style={{ display:"flex", gap:4 }}>
              {[
                { key:"all",       label:"All Orgs",      count:orgs.length },
                { key:"fleet",     label:"Fleet Management", count:fleetOrgs.length,    color:"#7C3AED" },
                { key:"free",      label:"Free Access",   count:freeAccessOrgs.length,  color:"#2563EB" },
                { key:"coupon",    label:"Coupon Code",   count:couponOrgs.length,      color:"#D97706" },
                { key:"suspended", label:"Suspended",     count:suspendedOrgs.length,   color:"#DC2626" },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    display:"flex", alignItems:"center", gap:5,
                    padding:"6px 12px", borderRadius:7, border:"none",
                    cursor:"pointer", fontSize:12, fontWeight:600,
                    background: activeTab === tab.key ? "#111827" : "#F3F4F6",
                    color:      activeTab === tab.key ? "#fff"    : "#6B7280",
                    transition:"all 0.15s",
                  }}
                >
                  {tab.label}
                  <span style={{
                    background: activeTab === tab.key ? "rgba(255,255,255,0.2)" : (tab.color ? `${tab.color}22` : "#E5E7EB"),
                    color:      activeTab === tab.key ? "#fff" : (tab.color || "#374151"),
                    borderRadius:99, padding:"1px 6px", fontSize:10, fontWeight:700,
                  }}>{tab.count}</span>
                </button>
              ))}
            </div>
            <input
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              placeholder="Search by name, email, coupon…"
              style={{ width:240, height:34, padding:"0 12px", background:"#F9FAFB", border:"1px solid #E5E7EB", borderRadius:7, fontSize:12, color:"#111827", outline:"none" }}
              onFocus={e => e.target.style.borderColor="#2563EB"}
              onBlur={e  => e.target.style.borderColor="#E5E7EB"}
            />
          </div>

          {/* Column headers */}
          <div style={{
            display:"grid",
            gridTemplateColumns: activeTab === "coupon" ? "1.8fr 1.8fr 0.8fr 0.8fr 1.4fr 1fr 1fr 80px" : "1.8fr 1.8fr 0.6fr 0.8fr 1.4fr 1fr 1fr 44px",
            padding:"8px 18px", background:"#F9FAFB", borderBottom:"1px solid #F3F4F6",
          }}>
            {(activeTab === "coupon"
              ? ["Organization","Email","Coupon","Joined","API Used / Left","Payment","Status","Actions"]
              : ["Organization","Email","Vehicles","Joined","API Used / Left","Access","Status",""]
            ).map(h => (
              <span key={h} style={{ fontSize:10, fontWeight:700, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:0.4 }}>{h}</span>
            ))}
          </div>

          {/* Loading */}
          {loading && (
            <div style={{ padding:"40px", textAlign:"center", color:"#9CA3AF", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
              <Loader2 size={16} style={{ animation:"spin 1s linear infinite" }} /> Loading…
            </div>
          )}

          {/* Empty */}
          {!loading && displayOrgs.length === 0 && (
            <div style={{ padding:"40px", textAlign:"center", color:"#9CA3AF", fontSize:14 }}>
              {orgs.length === 0 ? "No organizations registered yet." : "No results found."}
            </div>
          )}

          {/* Rows */}
          {!loading && displayOrgs.map((org, i) => {
            const paid     = org.payments.reduce((s, p) => s + p.amount, 0);
            const left     = Math.max(0, org.apiCallsTotal - org.apiCallsUsed);
            const pctUsed  = org.apiCallsTotal > 0 ? Math.round((org.apiCallsUsed / org.apiCallsTotal) * 100) : 0;
            const statusColor = org.isSuspended ? "#DC2626" : !org.apiCallsTotal ? "#9CA3AF" : left <= 0 ? "#DC2626" : pctUsed >= 70 ? "#D97706" : "#16A34A";
            const statusLabel = org.isSuspended ? "Suspended" : !org.apiCallsTotal ? "No Quota" : left <= 0 ? "Exhausted" : "Active";

            if (activeTab === "coupon") {
              const hasPaid = org.payments.length > 0;
              return (
                <motion.div
                  key={org.id}
                  initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:i*0.03 }}
                  onClick={() => setSelectedOrg(org)}
                  style={{
                    display:"grid",
                    gridTemplateColumns:"2fr 2fr 1fr 1fr 1.5fr 1fr 1fr 80px",
                    padding:"12px 18px",
                    borderBottom: i < displayOrgs.length-1 ? "1px solid #F9FAFB" : "none",
                    alignItems:"center",
                    background: org.isSuspended ? "#FFF5F5" : "transparent",
                    cursor:"pointer",
                  }}
                  onMouseOver={e => { e.currentTarget.style.background = org.isSuspended ? "#FEE2E2" : "#F0F4FF"; }}
                  onMouseOut={e  => { e.currentTarget.style.background = org.isSuspended ? "#FFF5F5" : "transparent"; }}
                >
                  <div style={{ fontSize:13, fontWeight:600, color:"#111827", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{org.name}</div>
                  <div style={{ fontSize:12, color:"#6B7280", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{org.email}</div>
                  <div>
                    <span style={{ fontSize:10, fontWeight:700, color:"#D97706", background:"#FEF3C7", border:"1px solid #FDE68A", borderRadius:4, padding:"2px 6px" }}>
                      {org.couponCode}
                    </span>
                  </div>
                  <div style={{ fontSize:12, color:"#6B7280" }}>{fmtDate(org.createdAt)}</div>
                  <div>
                    <div style={{ fontSize:12, color:"#374151" }}>
                      {org.apiCallsUsed} / {org.apiCallsTotal}
                      <span style={{ fontSize:11, color:"#9CA3AF", marginLeft:4 }}>({left} left)</span>
                    </div>
                    {org.apiCallsTotal > 0 && (
                      <div style={{ height:3, background:"#E5E7EB", borderRadius:99, marginTop:4, overflow:"hidden", width:70 }}>
                        <div style={{ height:"100%", width:`${pctUsed}%`, background:pctUsed>=90?"#DC2626":pctUsed>=70?"#D97706":"#16A34A", borderRadius:99 }} />
                      </div>
                    )}
                  </div>
                  <div>
                    {hasPaid ? (
                      <span style={{ fontSize:11, fontWeight:600, color:"#16A34A", display:"flex", alignItems:"center", gap:4 }}>
                        <CheckCircle size={11} /> ₹{paid.toLocaleString("en-IN")}
                      </span>
                    ) : (
                      <span style={{ fontSize:11, fontWeight:600, color:"#D97706", display:"flex", alignItems:"center", gap:4 }}>
                        <AlertTriangle size={11} /> Not Paid
                      </span>
                    )}
                  </div>
                  <div>
                    <span style={{
                      fontSize:11, fontWeight:600, padding:"3px 8px", borderRadius:99,
                      background: org.isSuspended ? "#FEF2F2" : statusColor === "#16A34A" ? "#F0FDF4" : statusColor === "#D97706" ? "#FFFBEB" : "#F9FAFB",
                      color: statusColor, border:`1px solid ${statusColor}30`,
                    }}>{statusLabel}</span>
                  </div>
                  <div style={{ display:"flex", gap:4 }}>
                    <button
                      onClick={() => handleAction(org.id, org.isSuspended ? "unsuspend" : "suspend")}
                      title={org.isSuspended ? "Restore access" : "Suspend account"}
                      style={{
                        width:30, height:30, borderRadius:7, border:"none",
                        background: org.isSuspended ? "#F0FDF4" : "#FEF2F2",
                        cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
                      }}
                    >
                      {org.isSuspended
                        ? <Shield size={13} color="#16A34A" />
                        : <ShieldOff size={13} color="#DC2626" />
                      }
                    </button>
                    <button
                      onClick={() => setSelectedOrg(org)}
                      style={{ width:30, height:30, borderRadius:7, border:"none", background:"#F3F4F6", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}
                    ><ChevronRight size={13} color="#6B7280" /></button>
                  </div>
                </motion.div>
              );
            }

            // Default "all", "free", and "suspended" tabs
            const accessLabel = org.isSuspended      ? { label:"Suspended",    bg:"#FEF2F2", color:"#DC2626", border:"#FECACA" }
                              : !org.hasPaid          ? { label:"No Quota",     bg:"#F9FAFB", color:"#9CA3AF", border:"#E5E7EB" }
                              : org.payments.length>0 ? { label:"Paid",         bg:"#F0FDF4", color:"#16A34A", border:"#BBF7D0" }
                              :                         { label:"Free Access",  bg:"#EFF6FF", color:"#2563EB", border:"#BFDBFE" };

            return (
              <motion.div
                key={org.id}
                initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:i*0.03 }}
                onClick={() => setSelectedOrg(org)}
                style={{
                  display:"grid",
                  gridTemplateColumns:"1.8fr 1.8fr 0.6fr 0.8fr 1.4fr 1fr 1fr 44px",
                  padding:"12px 18px",
                  borderBottom: i < displayOrgs.length-1 ? "1px solid #F9FAFB" : "none",
                  alignItems:"center",
                  background: org.isSuspended ? "#FFF5F5" : "transparent",
                  cursor:"pointer",
                }}
                onMouseOver={e => { e.currentTarget.style.background = org.isSuspended ? "#FEE2E2" : "#F0F4FF"; }}
                onMouseOut={e  => { e.currentTarget.style.background = org.isSuspended ? "#FFF5F5" : "transparent"; }}
              >
                {/* Name + badges */}
                <div style={{ display:"flex", alignItems:"center", gap:5, overflow:"hidden" }}>
                  <span style={{ fontSize:13, fontWeight:600, color:"#111827", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{org.name}</span>
                  {org.couponCode && (
                    <span style={{ fontSize:9, fontWeight:700, color:"#D97706", background:"#FEF3C7", borderRadius:3, padding:"1px 5px", flexShrink:0 }}>COUPON</span>
                  )}
                </div>

                {/* Email */}
                <div style={{ fontSize:12, color:"#6B7280", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{org.email}</div>

                {/* Vehicle count */}
                <div style={{ fontSize:13, fontWeight:600, color: (org.vehicles?.length||0)>0 ? "#111827" : "#9CA3AF" }}>
                  {(org.vehicles?.length||0) > 0
                    ? <span style={{ display:"flex", alignItems:"center", gap:3 }}>
                        <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><rect x="1" y="5" width="14" height="8" rx="2" stroke="#6B7280" strokeWidth="1.5"/><path d="M4 5V4a2 2 0 014 0v1M8 5V4a2 2 0 014 0v1" stroke="#6B7280" strokeWidth="1.5"/></svg>
                        {org.vehicles.length}
                      </span>
                    : "—"
                  }
                </div>

                {/* Joined */}
                <div style={{ fontSize:11, color:"#6B7280" }}>{fmtDate(org.createdAt)}</div>

                {/* API used / left */}
                <div>
                  {org.apiCallsTotal > 0 ? (
                    <>
                      <div style={{ fontSize:12, color:"#374151" }}>
                        <span style={{ fontWeight:600 }}>{org.apiCallsUsed}</span>
                        <span style={{ color:"#9CA3AF" }}> used · </span>
                        <span style={{ fontWeight:600, color: left<=0?"#DC2626":left<20?"#D97706":"#16A34A" }}>{left} left</span>
                      </div>
                      <div style={{ height:3, background:"#E5E7EB", borderRadius:99, marginTop:4, overflow:"hidden", width:72 }}>
                        <div style={{ height:"100%", width:`${pctUsed}%`, background:statusColor, borderRadius:99 }} />
                      </div>
                    </>
                  ) : (
                    <span style={{ fontSize:12, color:"#9CA3AF" }}>No quota assigned</span>
                  )}
                </div>

                {/* Access type */}
                <span style={{
                  fontSize:11, fontWeight:600, padding:"3px 8px", borderRadius:5,
                  background:accessLabel.bg, color:accessLabel.color,
                  border:`1px solid ${accessLabel.border}`,
                  whiteSpace:"nowrap",
                }}>{accessLabel.label}</span>

                {/* Status */}
                <div>
                  <span style={{
                    fontSize:11, fontWeight:600, padding:"3px 8px", borderRadius:99,
                    background: org.isSuspended ? "#FEF2F2" : statusColor === "#16A34A" ? "#F0FDF4" : statusColor === "#D97706" ? "#FFFBEB" : "#F9FAFB",
                    color: statusColor, border:`1px solid ${statusColor}30`,
                  }}>{statusLabel}</span>
                </div>

                <button
                  onClick={() => setSelectedOrg(org)}
                  style={{ background:"none", border:"none", cursor:"pointer", color:"#9CA3AF", display:"flex", alignItems:"center" }}
                  onMouseOver={e => e.currentTarget.style.color="#2563EB"}
                  onMouseOut={e  => e.currentTarget.style.color="#9CA3AF"}
                ><ChevronRight size={16} /></button>
              </motion.div>
            );
          })}
        </div>

        {/* Low balance warnings */}
        {orgs.filter(o => o.apiCallsTotal > 0 && (o.apiCallsTotal - o.apiCallsUsed) < 20 && !o.isSuspended).length > 0 && (
          <div style={{ marginTop:20, background:"#FFFBEB", border:"1px solid #FDE68A", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:10, fontSize:13, color:"#92400E" }}>
            <AlertCircle size={14} color="#D97706" style={{ flexShrink:0 }} />
            <span>
              <strong>{orgs.filter(o => o.apiCallsTotal > 0 && (o.apiCallsTotal - o.apiCallsUsed) < 20 && !o.isSuspended).length}</strong> organization(s) have fewer than 20 API calls remaining.
            </span>
          </div>
        )}

        {/* Free access users without payment */}
        {freeAccessOrgs.filter(o => !o.isSuspended).length > 0 && (
          <div style={{ marginTop:12, background:"#EFF6FF", border:"1px solid #BFDBFE", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, fontSize:13, color:"#1E40AF" }}>
              <Shield size={14} color="#2563EB" style={{ flexShrink:0 }} />
              <span>
                <strong>{freeAccessOrgs.filter(o=>!o.isSuspended).length}</strong> org(s) have free API access with no payment on record — review and request payment if needed.
              </span>
            </div>
            <button
              onClick={() => setActiveTab("free")}
              style={{ fontSize:12, fontWeight:600, color:"#2563EB", background:"none", border:"1px solid #BFDBFE", borderRadius:6, padding:"4px 12px", cursor:"pointer" }}
            >Review →</button>
          </div>
        )}
        </>)}
      </div>

      {/* Org modal */}
      <AnimatePresence>
        {selectedOrg && (
          <OrgDetailModal
            org={selectedOrg}
            onClose={() => setSelectedOrg(null)}
            onAction={handleAction}
          />
        )}
      </AnimatePresence>

      {/* Create org modal */}
      <AnimatePresence>
        {showCreateOrg && (
          <CreateOrgModal
            onClose={() => setShowCreateOrg(false)}
            onCreated={newOrg => {
              setActionMsg(`Account created: ${newOrg.email}`);
              setTimeout(() => setActionMsg(""), 5000);
              loadOrgs();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Main export ─────────────────────────────────────────── */
export default function AdminPage() {
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem("trackin_admin_token"));

  function handleLogout() {
    const token = sessionStorage.getItem("trackin_admin_token");
    if (token) {
      fetch(`${API_BASE}/api/admin/logout`, {
        method: "POST",
        headers: { "x-admin-token": token },
      }).catch(() => {});
    }
    sessionStorage.removeItem("trackin_admin_token");
    setAuthed(false);
  }

  if (!authed) return <AdminLogin onAuth={() => setAuthed(true)} />;
  return <AdminDashboard onLogout={handleLogout} />;
}
